<!-- src/pages/dictionary/Dictionary.vue -->
<template>
    <main class="dict">
        <h1 class="dict__title">용어 사전</h1>

        <!-- 초성 탭 -->
        <nav class="dict__tabs" role="tablist" aria-label="초성 선택">
            <button v-for="ch in initials" :key="ch" type="button" class="tab" :class="{ active: active === ch }"
                role="tab" :aria-selected="active === ch" @click="setActive(ch)">{{ ch }}</button>
        </nav>

        <!-- 단일 패널: active 값만 바꿔서 내용 교체 -->
        <section class="dict__panel" :key="active">
            <div class="t">
                <div class="t__head">
                    <div class="th th--no">번호</div>
                    <div class="th th--term">용어</div>
                </div>

                <ul class="t__body" v-if="entries[active] && entries[active].length">
                    <li v-for="(item, idx) in entries[active]" :key="idx" class="row" @click="toggle(idx)">
                        <button type="button" class="row__head" :aria-expanded="isOpen(idx)" >
                            <div class="td td--no">{{ idx + 1 }}</div>
                            <div class="td td--term">
                                <span class="term">{{ item.term }}</span>
                                <span v-if="item.en" class="term--en">({{ item.en }})</span>
                            </div>
                            <i class="caret" aria-hidden="true"></i>
                        </button>

                        <transition name="accord">
                            <div v-show="isOpen(idx)" class="row__panel">
                                <p v-for="(line, i) in item.desc" :key="i">{{ line }}</p>
                            </div>
                        </transition>
                    </li>
                </ul>

                <p class="empty" v-else>등록된 항목이 없습니다.</p>
            </div>
        </section>
    </main>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'

/* 초성 탭 */
const initials = ['ㄱ', 'ㄴ', 'ㄷ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅅ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅍ', 'ㅎ',
'A','B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'] 
const active = ref('ㄱ')
const setActive = (ch) => { active.value = ch }

/* 아코디언: 현재 탭이 바뀌면 열림 상태 초기화 */
const openSet = reactive(new Set())
const isOpen = (idx) => openSet.has(idx)
const toggle = (idx) => (isOpen(idx) ? openSet.delete(idx) : openSet.add(idx))
watch(active, () => openSet.clear())

/* 예시 데이터 (모든 탭에 최소 1개씩) */
const entries = {
    
    "ㄱ": [
        {
            "term": "감사가능성 위협",
            "en": "Auditability Threats",
            "desc": [
                "거래 처리 과정을 재구성하거나 검증하는 데 필요한 정보를 확보하지 못하게 되는 위험을 의미합니다.",
                "이러한 위협은 감사추적(Audit Trail), 백업(Backup) 및 기록 보존(Historical Records) 목적의 데이터 저장과 관련됩니다.",
                "데이터가 적절히 보관되지 않으면, 경영진·감사인·규제기관이 거래처리를 입증할 증거를 확보하지 못하는 위험이 발생할 수 있습니다."
            ]
        },
        {
            "term": "감사위원회",
            "en": "Audit Committee",
            "desc": [
                "감사위원회는 이사회 내에서 독립적인 위치를 갖는 조직으로, 최고경영진의 재무보고 책임 수행 여부를 검토하고 시정조치를 요구할 권한을 가집니다.",
                "또한 강력한 내부감사 기능과 함께, 경영진이 내부통제를 무력화하거나 재무성과를 왜곡하려는 행위를 식별하고 대응할 수 있는 조직 내 핵심 기구입니다."
            ]
        },
        {
            "term": "거래 충돌 매트릭스",
            "en": "Transaction Conflict Matrix",
            "desc": [
                "재무 관련 거래(Financial Transactions) 에 대한 적절한 직무분리(Segregation of Duties) 여부를 시각적으로 보여주는 도구입니다.",
                "매트릭스는 거래가 수행할 수 있는 기능(Function)과 영향을 미치는 재무계정(Account)을 대응시켜 표시합니다.",
                "동일한 거래가 하나의 계정에 대해 과도하게 많은 기능을 수행할 수 있을 경우,",
                "이는 통제 충돌(Control Conflict) 가능성을 나타내며, 내부통제의 약점(Risk of Control Weakness) 으로 간주됩니다."
            ]
        },
        {
            "term": "거래발생통제",
            "en": "Transaction Origination Controls",
            "desc": [
                "데이터가 시스템에 입력되기 전에 그 정확성과 완전성을 확보하기 위한 통제입니다.",
                "이 통제는 데이터가 기계 판독이 가능한 형식(Machine-Readable Format) 으로 변환되기 전까지의 절차에 적용됩니다.",
                "목표는 다음과 같습니다:",
                "- 입력 데이터의 유효성(Validity) 확보",
                "- 데이터의 신뢰성(Reliability) 확보",
                "- 정보의 비용 효율성(Cost-Effectiveness) 보장",
                "- 데이터가 손상 또는 조작되지 않도록 보호(Protection from Compromise)",
                "관리자, 시스템 담당자, 통제 설계자는 거래발생통제의 강화를 통해",
                "시스템 입력 단계에서부터 데이터 품질(Data Quality) 과 통제 신뢰성(Control Reliability) 을 확보해야 합니다."
            ]
        },
        {
            "term": "거래사용 매트릭스",
            "en": "Transaction Use Matrix",
            "desc": [
                "데이터 항목(Data Items) 과 해당 데이터를 사용하는 프로그램(Programs) 간의 관계를 나타내는 도표입니다.",
                "한쪽에는 직원 이름, 사번, 순급여, 사회보장번호 등의 데이터 항목이 나열되고,",
                "다른 쪽에는 프로그램이 수행하는 기능(Function) 이 표시됩니다.",
                "이를 통해 어떤 프로그램이 어떤 데이터에 접근·활용하는지 시각적으로 파악할 수 있습니다.",
                "이 매트릭스는 데이터 접근통제(Data Access Control) 및 권한 분리(Authorization Segregation) 평가에 활용됩니다."
            ]
        },
        {
            "term": "거래입력통제, IT",
            "en": "Transaction Entry Controls",
            "desc": [
                "비즈니스 거래가 발생한 시점부터 결과가 사용자에게 전달되기 전까지의 전 과정을 통제하는 절차입니다.",
                "이 통제는 데이터가 시스템에 입력될 때의 정확성과 완전성을 보장합니다.",
                "통제 범위에는 다음이 포함됩니다:",
                "- 데이터 통신 링크 입력 전까지의 통제(Pre-Communication Link Controls)",
                "- 비통신 환경에서의 응용프로그램 입력 전 통제(Application Input Controls)",
                "거래입력통제는 수작업 및 자동통제 절차의 조합으로 구성되며,",
                "특히 데이터 변환(Data Conversion), 검토 및 유효성 검증(Edit and Validation) 단계에서 중요합니다.",
                "최근에는 자동화된 통제(Auto-Control Routine) 의 비중이 커지고 있으며,",
                "이는 일관성(Consistency) 과 효율성(Efficiency) 을 향상시킵니다."
            ]
        },
        {
            "term": "거래처리위험",
            "en": "Transaction Processing Risk",
            "desc": [
                "단일 비즈니스 이벤트가 발생 시점부터 완료 시점까지 처리되는 과정에서 발생할 수 있는 위험입니다.",
                "즉, 거래의 입력, 처리, 저장, 보고 과정에서 오류, 누락, 부정행위, 시스템 실패 등이 발생할 위험을 포함합니다."
            ]
        },
        {
            "term": "거래통제",
            "en": "Transaction Controls",
            "desc": [
                "조직의 비즈니스 프로세스에서 발생하는 거래처리 위험(Transaction Processing Risks) 을 완화하기 위한 통제활동입니다.",
                "이 통제는 수작업(Manual) 또는 자동화(Automated) 형태로 운영될 수 있으며,",
                "다음의 정보처리 목적을 달성하도록 설계됩니다:",
                "- 완전성(Completeness)",
                "- 정확성(Accuracy)",
                "- 유효성(Validity)"
            ]
        },
        {
            "term": "경영 프로세스",
            "en": "Management Process",
            "desc": [
                "경영진이 조직을 운영하기 위해 수행하는 일련의 계획, 실행, 평가, 개선 활동의 흐름을 의미합니다.",
                "내부통제 시스템은 이러한 경영 프로세스의 통합적 구성요소(Integrated Component) 중 하나로 작동합니다."
            ]
        },
        {
            "term": "경영진 개입",
            "en": "Management Intervention",
            "desc": [
                "경영진이 비정상적이거나 비정기적인 거래 또는 사건에 대해,",
                "기존의 정책이나 절차를 정당한 목적(Legitimate Purpose) 으로 일시적으로 변경하거나 예외를 적용하는 행위를 의미합니다."
            ]
        },
        {
            "term": "MD&A, 경영진의 재무상태 및 영업실적 분석보고",
            "en": "Management’s Discussion and Analysis of Financial Condition and Results of Operations",
            "desc": [
                "연차보고서(Annual Report)의 한 섹션으로,",
                "투자자가 기업의 재무상태, 영업성과, 미래 전망을 이해할 수 있도록",
                "경영진이 작성하는 설명 및 분석 보고서입니다."
            ]
        },
        {
            "term": "경영진의 통제무력화",
            "en": "Management Override",
            "desc": [
                "경영진이 개인적 이익 또는 조직의 재무상태나 준수현황을 왜곡하여 유리하게 보이게 하려는 목적으로",
                "기존의 정책이나 절차를 부당하게(overrule) 무시하거나 변경하는 행위를 말합니다.",
                "이는 내부통제의 신뢰성을 훼손하는 대표적인 위험 요소입니다."
            ]
        },
        {
            "term": "고위경영진",
            "en": "Senior Management",
            "desc": [
                "조직의 최고경영자(CEO) 또는 그에 준하는 최고 의사결정자와",
                "그를 보좌하는 고위관리팀(Senior Management Team) 을 포함합니다.",
                "이들은 조직의 전략, 통제, 위험관리 등 핵심 경영활동에 대한 최종 책임을 집니다."
            ]
        },
        {
            "term": "고유위험 / 내재위험",
            "en": "Inherent Risk",
            "desc": [
                "경영진이 위험의 발생 가능성(Likelihood) 이나 영향(Impact) 을 완화하기 위한 어떠한 조치도 취하지 않은 상태에서,",
                "조직의 목표 달성에 영향을 미칠 수 있는 본질적 위험 수준을 의미합니다."
            ]
        },
        {
            "term": "CPAs, 공인회계사",
            "en": "Certified Public Accountants",
            "desc": [
                "공중(General Public)을 대상으로 서비스를 제공하는 자격을 갖춘 전문 회계인(Licensed Professional Accountants) 을 말합니다."
            ]
        },
        {
            "term": "공시 원칙",
            "en": "Disclosure Principle",
            "desc": [
                "기업의 재무제표는 외부 이해관계자가 기업에 대해 충분히 이해하고 합리적인 의사결정을 내릴 수 있도록 충분한 정보를 제공해야 한다는 원칙입니다."
            ]
        },
        {
            "term": "공시 통제 및 절차",
            "en": "Disclosure Controls and Procedures",
            "desc": [
                "미국 증권거래위원회(SEC) 의 인증 규칙에 정의된 개념으로,",
                "발행자가 거래법(Exchange Act) 에 따라 보고해야 하는 정보가 기한 내에 기록·처리·요약·보고되도록 보장하는 통제 및 절차를 의미합니다.",
                "이는 또한, 공시 대상 정보가 적시에 경영진에게 전달되어 필요한 공시 결정을 내릴 수 있도록 보장하는 절차를 포함합니다."
            ]
        },
        {
            "term": "CBOK, 공통지식체계",
            "en": "Common Body of Knowledge",
            "desc": [
                "내부통제 자격인증의 기준이 되는 핵심 역량(Skills)과 지식영역을 정의한 체계입니다.",
                "CBOK는 각 내부통제 분야의 전문역량과 기술요소를 분류·정의한 지식 집합으로 구성됩니다."
            ]
        },
        {
            "term": "관련 원칙",
            "en": "Relevant Principle",
            "desc": [
                "‘원칙(Principles)’은 내부통제 구성요소(Components)에 연계된 기본 개념(Fundamental Concept) 을 의미합니다.",
                "다만, 특정 산업, 운영, 또는 규제 환경에서는 일부 원칙이 특정 구성요소와 직접적으로 관련되지 않을 수도 있음을 인정합니다."
            ]
        },
        {
            "term": "관리통제",
            "en": "Administrative Control",
            "desc": [
                "관리통제는 조직의 계획(Plan), 절차(Procedures), 기록(Records) 등을 포함하며,",
                "이는 경영진의 승인(Authorization) 과 관련된 의사결정 과정을 다룹니다.",
                "이러한 승인 과정은 조직 목표 달성에 대한 경영진의 책임과 직접적으로 연관되며,",
                "회계통제를 수립하는 출발점이 됩니다."
            ]
        },
        {
            "term": "구성요소",
            "en": "Component",
            "desc": [
                "내부통제의 다섯 가지 핵심 요소 중 하나를 의미합니다.",
                "내부통제의 구성요소에는 통제환경(Control Environment), 위험평가(Risk Assessment),",
                "통제활동(Control Activities),", 
                "정보와 의사소통(Information and Communication), 모니터링활동(Monitoring Activities) 이 포함됩니다."
            ]
        },
        {
            "term": "CICP 국제공인 전문내부통제사",
            "en": "Certified Internal Control Professional",
            "desc": [
                "내부통제 분야의 고급 전문가를 인증하기 위한 프로그램으로, 내부통제 역량과 실무 경험이 일정 수준 이상임을 검증합니다.",
                "CICP 또한 독립된 인증위원회가 주관하며, 국제내부통제협회(ICI)가 운영합니다."
            ]
        },
        {
            "term": "CICS 국제공인 내부통제사",
            "en": "Certified Internal Control Specialist",
            "desc": [
                "내부통제 분야의 선도 전문가들이 개발한 자격 프로그램으로, 내부통제 역량이 일정 수준 이상임을 공식적으로 인정받은 전문가에게 부여됩니다.",
                "CICS 프로그램은 독립적인 인증위원회(Certification Board)가 주관하고, 국제내부통제협회(ICI, Internal Control Institute) 가 관리 및 운영합니다."
            ]
        },
        {
            "term": "IFRS, 국제재무보고기준",
            "en": "International Financial Reporting Standards",
            "desc": [
                "국제회계기준위원회(IASB) 에 의해 제정된 전 세계적으로 통용되는 회계기준입니다.",
                "IFRS는 국가 간 회계기준의 일관성을 높이고, 투자자와 이해관계자들이 비교 가능한 재무정보를 활용할 수 있도록 설계되었습니다."
            ]
        },
        {
            "term": "IASB, 국제회계기준위원회",
            "en": "International Accounting Standards Board",
            "desc": [
                "국제재무보고기준(IFRS) 의 제정과 운영을 감독하는 민간 국제기구로,",
                "글로벌 회계기준의 일관성과 투명성을 확보하기 위한 역할을 수행합니다."
            ]
        },
        {
            "term": "근거문서 / 원천문서",
            "en": "Source Document",
            "desc": [
                "회계거래를 기록하기 위한 증빙자료(Evidence)와 데이터(Data) 를 제공하는 문서입니다.",
                "예: 송장(Invoice), 영수증(Receipt), 구매주문서(Purchase Order), 입금전표 등",
                "이 문서는 거래의 발생 근거와 정확성을 입증하는 데 사용됩니다."
            ]
        },
        {
            "term": "기록",
            "en": "Recording",
            "desc": [
                "거래(Transaction)는 재무제표 작성에 필요한 정보를 제공하고,",
                "일반적으로 인정된 회계원칙(GAAP) 또는 기타 적용 가능한 기준에 따라 보고할 수 있도록 적절히 기록되어야 합니다.",
                "또한, 자산의 책임성과 추적 가능성(Accountability for Assets) 을 유지하기 위해서도 기록이 필요합니다."
            ]
        },
        {
            "term": "기술 — 소프트웨어",
            "en": "Technology — Software",
            "desc": [
                "컴퓨터에서 실행되는 응용프로그램(Application), 제조 통제시스템(Manufacturing Control Systems) 등을 포함한",
                "소프트웨어 기술 전반을 의미합니다."
            ]
        },
        {
            "term": "기술 일반통제 / IT 일반통제, ITGC",
            "en": "Technology General Controls",
            "desc": [
                "조직의 기술 인프라가 지속적이고 적절하게 운영되도록 보장하는 통제활동을 의미합니다.",
                "이에는 다음과 같은 영역이 포함됩니다:",
                "- 기술 인프라 통제(Technology Infrastructure Controls)",
                "- 보안관리(Security Management)",
                "- 기술의 획득·개발·유지관리(Acquisition, Development, and Maintenance Controls)",
                "또한, 이 통제는 일반 전산통제(General Computer Controls) 또는 정보기술통제(Information Technology Controls) 라고도 불립니다."
            ]
        },
        {
            "term": "기업가치",
            "en": "Corporate Values",
            "desc": [
                "기업이 일상적인 운영에서 중요하게 여기는 핵심 가치(Core Values) 를 의미합니다.",
                "예를 들어 “모든 구성원을 존중한다”는 가치가 있다면, 직원의 부적절한 행동을 공개적으로 질책하는 것은",
                "그 가치에 위배되는 행위가 됩니다."
            ]
        },
        {
            "term": "COSO 기업위험관리 프레임워크",
            "en": "COSO’s Enterprise Risk Management Framework",
            "desc": [
                "조직의 비즈니스 목표를 달성하기 위한 구조적 체계를 제공합니다.",
                "경영진은 조직의 사명(Mission)과 제약조건(Constraints)에 부합하면서도",
                "목표 달성 가능성을 높이는 전략적 비즈니스 목표를 수립할 책임이 있습니다.",
                "COSO의 기업위험관리 프레임워크(ERM Framework) 는 이러한 목표를 설정하고 달성하기 위한",
                "체계적인 위험관리 및 통제 접근방식을 제시합니다."
            ]
        },
        {
            "term": "기업준법위원회",
            "en": "Corporate Compliance Committee",
            "desc": [
                "기업의 준법감시기능(Compliance Function) 을 강화하기 위해 설치된 위원회로,",
                "각 사업부문 대표들이 참여하여 준법 전략과 시스템을 감독합니다.",
                "위원회는 준법책임자, 이사회, 최고경영진에게 주요 준법 이슈, 위험, 동향을 보고하고 관리합니다."
            ]
        },
        {
            "term": "기업준법책임자",
            "en": "Corporate Compliance Officer",
            "desc": [
                "기업의 준법 시스템을 총괄 감독하며, 내부감사·모니터링·자체평가 프로그램을 통해",
                "조직이 법적·규제적 의무를 준수하도록 보장합니다.",
                "준법책임자는 기업 전반에서 표준의 일관된 적용과 해석을 유지하고,",
                "일반적으로 이사회 의장, CEO, 감사위원회에 직접 보고합니다."
            ]
        },
        {
            "term": "기업지배구조",
            "en": "Corporate Governance",
            "desc": [
                "기업지배구조는 보편적 정의가 존재하지 않으나, 일반적으로 조직의 경영활동을 통제·감독·지도하는 구조와 절차를 의미합니다.",
                "사전적 의미로 “통치(govern)”는 권한을 행사하고, 행동을 지도하며, 통제하는 것을 뜻합니다.",
                "기업지배구조는 주로 이사회(Board of Directors) 와 경영진(Senior Management) 이",
                "기업을 건전하게 운영하기 위해 수행하는 관리활동의 집합으로 이해됩니다."
            ]
        }
    ],
    "ㄴ": [
        {
            "term": "내부감사",
            "en": "Internal Auditing",
            "desc": [
                "조직 내부에 설치된 기능으로, 조직의 운영성과, 통제의 적정성 및 준수 여부를 평가할 권한과 책임을 가집니다.",
                "국제내부감사인협회(IIA) 의 「내부감사 전문실무기준(Standards for the Professional Practice of Internal Auditing)」에 따르면,",
                "내부감사는 조직의 가치를 높이고 개선하기 위한 독립적이고 객관적인 보증(Assurance) 및 자문(Consulting) 활동입니다.",
                "내부감사는 체계적이고 규율된 접근법(Systematic and Disciplined Approach) 을 통해",
                "조직의 위험관리, 통제, 거버넌스 프로세스의 효과성을 평가하고 개선합니다."
            ]
        },
        {
            "term": "내부감사 헌장",
            "en": "Internal Audit Charter",
            "desc": [
                "이사회(Board of Directors)가 검토·승인한 공식 문서로,",
                "내부감사 기능의 권한(Authority) 과 책임(Responsibility) 을 명시합니다.",
                "내부감사 헌장은 감사부서가 경영진 및/또는 이사회에 독립적으로 보고할 권한을 규정합니다."
            ]
        },
        {
            "term": "내부감사인",
            "en": "Internal Auditors",
            "desc": [
                "조직의 직원으로서, 회사의 정책 준수 여부, 법규 준수 상태, 운영의 효율성을 점검합니다.",
                "내부감사인은 내부통제 시스템을 직접 검토하고 개선사항을 제안합니다.",
                "IIA의 기준에 따르면, 내부감사의 범위에는 내부통제 시스템의 적정성 및 효과성 평가,",
                "그리고 각 부문의 성과 품질에 대한 평가가 포함됩니다."
            ]
        },
        {
            "term": "내부요인 기반 위험평가 Using Internal Application Characteristics",
            "en": "Risk Scoring",
            "desc": [
                "응용프로그램이 처리하는 데이터의 특성(Data Characteristics) 과",
                "그 데이터를 통제하는 내부통제수준(Control Effectiveness) 을 평가하여",
                "응용프로그램의 내재적 위험 수준(Inherent Risk Level) 을 결정하는 방법입니다."
            ]
        },
        {
            "term": "내부통제",
            "en": "Internal Control",
            "desc": [
                "미국공인회계사협회(AICPA) 의 정의에 따르면,",
                "내부통제는 조직 내에서 채택된 계획(Plan), 절차(Procedures), 방법(Measures) 의 집합으로,",
                "다음의 목적을 달성하기 위해 설계됩니다:",
                "- 자산의 보호(Safeguarding of Assets)",
                "- 회계정보의 정확성과 신뢰성 확보(Accuracy and Reliability of Accounting Data)",
                "- 운영 효율성 증진(Operational Efficiency)",
                "- 경영방침의 준수(Adherence to Managerial Policies)",
                "이 정의는 내부통제가 회계부서나 재무부서의 기능을 넘어, 조직 전체의 통합적 관리체계임을 강조합니다."
            ]
        },
        {
            "term": "내부통제 결함",
            "en": "Internal Control Deficiency",
            "desc": [
                "내부통제 구성요소 또는 관련 원칙 중 하나 이상에 존재하는 부족함이나 결함으로 인해,",
                "조직이 목표를 달성할 가능성을 낮추는 상태를 의미합니다."
            ]
        },
        {
            "term": "내부통제 보고서",
            "en": "Internal Control Report",
            "desc": [
                "경영진이 작성하는 보고서로, 재무보고에 대한 내부통제의 적정성 및 그에 대한 경영진의 책임을 기술합니다."
            ]
        },
        {
            "term": "내부통제 시스템 설계",
            "en": "Internal Control System Design",
            "desc": [
                "1. 의도(Intent): 내부통제의 목적은 조직이 목표를 달성할 수 있도록 합리적 보증을 제공하는 것입니다.",
                "이 의도가 실현될 경우 시스템은 효과적인 것으로 간주됩니다.",
                "2. 계획(Plan): 시스템이 설계된 방식(How it is supposed to work)과 실제 운영 방식(How it actually works)을 구분하여 관리하는 것을 의미합니다."
            ]
        },
        {
            "term": "COSO 내부통제 프레임워크",
            "en": "COSO’s Internal Control Framework",
            "desc": [
                "COSO 내부통제 프레임워크는 미국 증권거래위원회(SEC) 에 의해 내부통제를 평가하기 위한 유효한 기준으로 공식 인정받고 있습니다.",
                "이 프레임워크는 경영진이 조직을 운영하는 방식에서 도출된 상호 연관된 다섯 가지 구성요소로 이루어져 있으며, 경영 프로세스 전반에 통합되어 있습니다.",
                "다섯 가지 구성요소는 통제환경(Control Environment), 위험평가(Risk Assessment), 통제활동(Control Activities), ",
                "정보와 의사소통(Information and Communication), 모니터링(Monitoring Activities) 입니다.",
                "COSO 내부통제 프레임워크는 현재 전 세계적으로 가장 널리 인정받는 내부통제 모델로 자리 잡았습니다."
            ]
        },
        {
            "term": "내재적 한계",
            "en": "Inherent Limitations",
            "desc": [
                "모든 내부통제 시스템이 가지는 한계적 특성을 의미합니다.",
                "이에는 내부통제의 전제조건, 조직이 통제할 수 없는 외부 사건, 인간 판단의 한계, 시스템 오류나 통제 실패 가능성,",
                "그리고 경영진의 통제 무력화(Management Override) 및 담합(Collusion) 가능성이 포함됩니다."
            ]
        },
        {
            "term": "네트워크",
            "en": "Network",
            "desc": [
                "서로 다른 컴퓨터들이 동일한 정보를 공유할 수 있도록 전자적으로 연결된 시스템을 의미합니다.",
                "즉, 데이터와 자원을 교환하기 위해 구성된 정보통신 연결망입니다."
            ]
        },
        {
            "term": "노출 / 위험노출",
            "en": "Exposure",
            "desc": [
                "‘노출’이란 조직이 손실에 노출될 수 있는 사건 또는 상황을 의미합니다.",
                "이는 실제 손실을 의미하는 것은 아니며, 손실이 발생할 가능성(Potential for Loss) 이 존재함을 나타냅니다.",
                "예를 들어, “무단거래(Unauthorized Transactions)”라는 노출은 손실이 발생했다는 의미가 아니라,",
                "해당 거래가 시스템 내에서 탐지·삭제되어 손실이 방지될 수도 있음을 의미합니다.",
                "즉, 노출은 위협(Threat)으로부터 발생할 수 있는 잠재적 손실 규모(금전적 또는 비금전적 가치) 를 나타냅니다."
            ]
        }
    ],
    "ㄷ": [
        {
            "term": "담합 / 공모",
            "en": "Collusion",
            "desc": [
                "두 명 이상이 공모하여 내부통제를 회피하거나 무력화하고, 조직을 부정행위로 손해 입히는 행위를 말합니다."
            ]
        },
        {
            "term": "데이터 저장 및 검색 통제",
            "en": "Data Storage and Retrieval Controls",
            "desc": [
                "데이터 저장 및 검색 통제는 파일 처리 및 파일 오류 처리 과정에서 적용되는 통제활동을 포함합니다.",
                "이는 응용 시스템의 전산처리와 직접 관련되지 않은 파일 관리 절차를 통제하며, 데이터 저장 및 검색 중 데이터 파일과 프로그램의 무결성을 유지하기 위한 시설 관리 및 인력 절차가 중요합니다.",
                "이 영역은 사람의 개입이 많고 수작업 데이터 처리 비중이 높기 때문에, 통제 절차의 일관성과 정확성이 특히 강조됩니다."
            ]
        },
        {
            "term": "데이터 통신 통제",
            "en": "Data Communications Controls",
            "desc": [
                "데이터 통신 통제는 메시지 입력 장치에서 수신 장치로 데이터를 전송하는 과정에서 데이터의 무결성을 보장하기 위한 통제입니다.",
                "이러한 통제는 대부분의 데이터 통신 장비가 송신 또는 수신 기관이 아닌 외부 기관에 의해 소유·관리되기 때문에 중요합니다.",
                "또한 최근 많은 조직이 데이터 통신 서비스를 응용 시스템의 필수 구성요소로 활용함에 따라, 전체 시스템의 데이터 정확성과 완전성을 확보하기 위해 더욱 중요해지고 있습니다."
            ]
        }
    ],
    "ㅁ": [
        {
            "term": "말콤 볼드리지 국가품질상",
            "en": "Malcolm Baldrige National Quality Award",
            "desc": [
                "미국 기업의 경영 효율성과 효과성 향상을 촉진하기 위해 제정된 말콤 볼드리지 경영모델(Malcolm Baldrige Management Model) 을 기반으로 한 국가 품질상입니다.",
                "이 상은 일본의 데밍상(Deming Prize) 에서 영감을 받아 설립되었으며,",
                "두 상의 목적은 다음 두 가지입니다.",
                "1. 자신의 산업 분야에서 선도적인 기업을 인정(Recognition)",
                "2. 우수 사례(Best Practices)를 공유하여 다른 기업의 경영성과를 향상(Improvement)"
            ]
        },
        {
            "term": "SEC, 미국 증권거래위원회",
            "en": "Securities and Exchange Commission",
            "desc": [
                "미국의 연방정부 기관으로, 증권시장과 투자자 보호, 기업공시의 투명성 확보,",
                "회계감독 및 기업규제 집행을 담당합니다.",
                "즉, 미국 내 금융시장을 감독하고 기업의 공시자료를 검토하는 대표적 규제기관(Regulatory Agency) 입니다."
            ]
        },
        {
            "term": "모니터링",
            "en": "Monitoring",
            "desc": [
                "내부통제 시스템의 품질과 효과성을 지속적으로 평가하는 과정입니다.",
                "이는 다음 두 가지 형태로 수행됩니다.",
                "- 상시 모니터링(Ongoing Monitoring):",
                "일상적인 운영활동 속에서 이루어지는 경영진 및 감독활동,",
                "그리고 직원이 업무 수행 중 수행하는 점검활동 등을 포함합니다.",
                "- 별도평가(Separate Evaluations):",
                "정기적 또는 비정기적으로 수행되는 독립적인 통제점검 및 평가활동으로,",
                "수행 빈도와 범위는 위험 수준과 기존 모니터링의 효과성에 따라 결정됩니다.",
                "모니터링 결과 식별된 내부통제 결함은 상위 보고 체계(Upstream Reporting) 를 통해 전달되어야 하며,",
                "중대한 사안은 반드시 최고경영진 및 이사회(Board of Directors) 에 보고되어야 합니다."
            ]
        }
    ],
    "ㅂ": [
        {
            "term": "법인",
            "en": "Corporation",
            "desc": [
                "국가 또는 주(州) 법률에 의해 설립된 독립적인 법적 실체(Legal Entity) 로,",
                "소유주와 분리된 권리와 의무를 가집니다."
            ]
        },
        {
            "term": "벤치마킹",
            "en": "Benchmarking",
            "desc": [
                "다른 조직의 우수사례(Best Practices) 와 비교하여 자사 성과를 평가·분석하는 활동을 의미합니다.",
                "이를 통해 효율성, 품질, 경쟁력 향상을 위한 개선 방향을 도출합니다."
            ]
        },
        {
            "term": "보상위원회",
            "en": "Compensation Committee",
            "desc": [
                "이사회 산하 위원회로, 조직의 보상체계가 장기적 성과를 저해하지 않도록 설계·감독하는 역할을 합니다.",
                "즉, 단기성과에 치우치지 않고 조직의 지속가능한 목표 달성에 부합하는 보상정책을 관리합니다."
            ]
        },
        {
            "term": "분권형 조직",
            "en": "Decentralized Company",
            "desc": [
                "사업 부문별로 나뉘어 있으며, 각 부문 관리자가 해당 부문의 계획, 운영, 통제와 관련된 의사결정 권한을 가지는 기업 구조를 말합니다."
            ]
        }
    ],
    "ㅅ": [
        {
            "term": "사베인즈-옥슬리법, SOX법",
            "en": "Sarbanes-Oxley Act",
            "desc": [
                "미국에서 제정된 이 법은 대형 기업 회계부정 및 경영 스캔들(예: 엔론, 월드컴 등) 이후 기업의 책임성과 투명성(Accountability and Transparency) 을 강화하기 위해 마련되었습니다.",
                "이 법의 주요 목적은 기업의 윤리적 경영 기준을 강화하고, 경영진과 이사회에 대한 책임(Responsibility) 을 명확히 하는 것입니다.",
                "SOX법은 경영진에게 다음을 요구합니다:",
                "- 내부통제(Internal Control) 를 정기적으로 검토할 것",
                "- 재무보고의 정확성(Accuracy) 및 완전성(Completeness) 에 대해 책임질 것",
                "또한, 법 위반 시 엄격한 형사 및 민사 처벌을 부과합니다.",
                "이 법은 기업의 경영진(Management) 및 이사회(Board of Directors) 의 책무를 강화하여,",
                "미국 기업지배구조의 신뢰 회복에 큰 전환점을 마련했습니다."
            ]
        },
        {
            "term": "생산 또는 전환 사이클",
            "en": "Production or Conversion Cycle",
            "desc": [
                "조직의 생산계획 및 통제(Production Planning & Control), 재고관리(Inventory Planning & Control),",
                "자산 및 이연비용 회계(Property and Deferred Cost Accounting), 원가회계(Cost Accounting) 등의 기능을 포함하는 사이클입니다.",
                "즉, 자원(원재료, 노동력 등)을 제품 또는 서비스로 전환하는 과정 전반을 포괄합니다."
            ]
        },
        {
            "term": "서버",
            "en": "Server",
            "desc": [
                "조직의 데이터가 저장되어 있으며, 여러 컴퓨터가 이를 공유·접근할 수 있도록 하는 중앙 컴퓨터 시스템입니다.",
                "서버는 데이터의 저장, 관리, 접근제어 및 보안 유지를 담당합니다."
            ]
        },
        {
            "term": "수익 사이클",
            "en": "Revenue Cycle",
            "desc": [
                "조직의 수익창출 활동과 관련된 전 과정을 의미하며, 다음의 기능들을 포함합니다:",
                "- 상품·서비스 요청 접수 및 승인(Receiving and Accepting Orders)",
                "- 상품 인도 및 서비스 제공(Delivery/Provision)",
                "- 신용 승인 및 현금수취(Credit Granting & Cash Receipts)",
                "- 청구 및 수금(Billing & Collection)",
                "- 수익, 외상매출금, 커미션, 보증, 대손, 반품 등의 회계처리(Accounting for Revenue-Related Items)"
            ]
        },
        {
            "term": "수작업 통제",
            "en": "Manual Controls",
            "desc": [
                "기술적 자동화가 아닌, 사람이 직접 수행하는 통제활동을 의미합니다.",
                "예: 서면 승인, 수동 대사(Reconciliation), 문서 검토 등",
                "이는 자동통제(Automated Controls) 와 대비되는 개념입니다."
            ]
        },
        {
            "term": "승인",
            "en": "Authorization",
            "desc": [
                "거래는 경영진의 일반적 또는 개별적 승인을 받아 수행되어야 합니다.",
                "- 일반 승인(General Authorization): 경영진이 정책을 수립하면서 하위 구성원에게 일정 한도 내 거래를 허용하는 경우",
                "- 개별 승인(Specific Authorization): 비정기적이거나 중요도가 높은 거래(예: 대규모 투자지출 등)에 대해 경영진이 사안별로 직접 검토·승인하는 경우",
                "이처럼 승인은 조직의 정책 준수와 통제의 핵심 수단으로 작용합니다."
            ]
        },
        {
            "term": "시스템 통제 / 프로세스 통제",
            "en": "System Controls",
            "desc": [
                "개별 응용시스템(Application System) 의 관리 측면을 정의하는 통제활동입니다.",
                "예를 들어, 급여시스템(Payroll System)의 변경이 효율적이고 정확하게 수행되도록 보장하는 절차,",
                "시스템 구조(System Architecture) 및 문서관리 체계 등을 포함합니다.",
                "또한, 경영진이 시스템 전반을 관리하기 위해 필요한 핵심성과지표(Key Indicators) 를 정의하며,",
                "독립적인 대사(Reconciliation) 를 통해 처리의 정확성을 검증합니다.",
                "시스템 통제의 책임은 주로 중간관리자(Middle Management) 가 집니다."
            ]
        },
        {
            "term": "시정통제",
            "en": "Corrective Control",
            "desc": [
                "바람직하지 않은 사건이 발생했을 때, 이를 수정하거나 원인을 제거하기 위한 정보나 증거를 제공하는 통제활동입니다.",
                "시정통제는 일반적으로 탐지통제(Detection Control) 의 결과를 기반으로 적용됩니다."
            ]
        },
        {
            "term": "실행된 / 적용된",
            "en": "Effected",
            "desc": [
                "내부통제 시스템과 관련하여 사용되는 용어로, ‘수립되고 유지되는(devised and maintained)’ 상태를 의미합니다."
            ]
        }
    ],
    "ㅇ": [
        {
            "term": "업무 시스템/응용 프로그램 위험",
            "en": "Business System/Application Risk",
            "desc": [
                "조직의 운영과 관련된 활동에서 발생할 수 있는 위험을 의미합니다.",
                "이러한 운영은 일반적으로 비즈니스 사이클(Business Cycle) 형태로 표현되며,",
                "예를 들어 수익 및 비용 사이클(Revenue and Expense Cycle) 과 그 안의 **세부 응용 프로세스(예: 청구, 구매 등)**를 포함합니다.",
                "즉, 비즈니스 시스템이나 애플리케이션의 운영상 결함 또는 오류로 인해 조직의 업무 수행과 통제에 영향을 미칠 수 있는 위험을 의미합니다."
            ]
        },
        {
            "term": "예방 통제",
            "en": "Preventive Controls",
            "desc": [
                "통제는 일반적으로 설계 목적에 따라 탐지통제(Detective Controls) 와 예방통제(Preventive Controls) 로 구분됩니다.",
                "예방통제는 오류나 부정행위가 발생하기 전에 이를 방지하도록 설계된 통제활동을 의미합니다.",
                "이러한 통제는 시스템이나 절차 내에 내재적으로 포함되어 있어 눈에 잘 띄지 않을 수도 있지만,",
                "문제 발생 가능성을 사전에 차단하는 핵심적인 역할을 합니다."
            ]
        },
        {
            "term": "오류의 연쇄 발생",
            "en": "Cascading of Errors",
            "desc": [
                "전산화된 업무 응용 시스템에서 발생하는 특유의 문제로, 하나의 오류가 연쇄적으로 다른 오류를 유발하는 현상을 의미합니다.",
                "이러한 오류는 사전에 예방하거나 탐지하기가 어렵다는 특징이 있습니다."
            ]
        },
        {
            "term": "외부감사",
            "en": "External Auditing",
            "desc": [
                "독립된 공인회계사(Independent Certified Public Accountants)가 수행하는 감사로,",
                "조직의 재무보고 목적 달성에 기여합니다.",
                "외부감사인은 객관적이고 독립적인 시각을 제공하여, 경영진과 이사회가 재무보고 책임을 충실히 이행하도록 지원합니다."
            ]
        },
        {
            "term": "외부감사인",
            "en": "External Auditor",
            "desc": [
                "기업과 독립된 외부 회계전문가로서, 내부통제를 평가하고 재무제표가 일반적으로 인정된 회계원칙(GAAP) 에 따라 공정하게 작성되었는지를 검증합니다."
            ]
        },
        {
            "term": "외부요인 기반 위험평가 Using External Application Characteristics",
            "en": "Risk Scoring",
            "desc": [
                "응용프로그램이 개발·운영·사용되는 환경적 특성(Environmental Characteristics) 을 평가하여",
                "해당 응용프로그램의 위험 수준을 산정(Determine) 하는 방법입니다."
            ]
        },
        {
            "term": "외부재무보고 사이클",
            "en": "External Financial Reporting Cycle",
            "desc": [
                "외부재무보고 사이클은 다음과 같은 절차를 포함하는 과정입니다:",
                "- 분개 및 거래의 일반원장 반영",
                "- 적용할 회계기준(GAAP)의 결정",
                "- 재무제표 및 외부보고서 작성을 위한 정보 수집 및 통합",
                "- 재무제표 및 관련 공시사항의 작성·검토",
                "이 사이클은 사베인즈-옥슬리법(Sarbanes-Oxley Act) 제정 이후, 기업의 투명성과 책임성 강화를 위해 더욱 중요해졌습니다."
            ]
        },
        {
            "term": "운영성과지표",
            "en": "Operational Performance Measure",
            "desc": [
                "재무적 지표가 아닌 비재무적 성과 측정지표로, 조직의 효율성(Efficiency) 과 효과성(Effectiveness) 을 평가하는 데 사용됩니다.",
                "이는 모든 사업 부문이 회사의 목표를 달성하기 위해 유기적으로 협력하고 있는지를 판단하는 기준입니다."
            ]
        },
        {
            "term": "운영활동",
            "en": "Operations",
            "desc": [
                "‘운영목표(Operational Objectives)’ 또는 ‘운영통제(Operational Controls)’와 함께 사용되며,",
                "조직의 운영 효율성 및 효과성, 성과 및 수익성 목표, 그리고 자원의 보호(Safeguarding of Resources) 와 관련된 활동을 의미합니다."
            ]
        },
        {
            "term": "위험",
            "en": "Risk",
            "desc": [
                "위험이란 잠재적인 위협(Threat) 이 실제로 발생하여 조직에 부정적인 영향을 미칠 가능성(Likelihood)을 의미합니다.",
                "예를 들어, 허리케인 접근 시 기업 데이터센터 파손의 위험이 증가하는 것처럼,",
                "위험은 항상 발생 확률(Probability) 또는 발생 빈도(Frequency) 와 연관되어 있습니다."
            ]
        },
        {
            "term": "위험관리",
            "en": "Risk Management",
            "desc": [
                "위험관리는 최고경영진(Senior Management) 의 책임 하에 수행되는 공식적이고 체계적인 과정으로,",
                "위험을 식별(Identify) → 측정(Measure) → 우선순위 결정(Prioritize) 하여",
                "효율적으로 관리할 수 있도록 하는 절차를 포함합니다."
            ]
        },
        {
            "term": "위험노출–위협 도식",
            "en": "Risk Exposure – Threat Diagram",
            "desc": [
                "- Risk(위험) 은 바람직하지 않은 사건이 발생할 확률(Probability) 을 의미합니다.",
                "- Threat(위협) 은 손실을 유발할 수 있는 사건 또는 활동의 원인(Cause) 을 의미합니다.",
                "- Exposure(노출) 은 위협이 현실화되었을 때 발생할 수 있는 잠재적 손실(Potential Loss) 을 나타냅니다.",
                "따라서 위험노출–위협 도식은 이러한 원인-결과 관계(Cause–Effect Relationship) 를 시각적으로 보여주어, 위험의 구성요소를 명확히 설명합니다."
            ]
        },
        {
            "term": "위험대응",
            "en": "Risk Response",
            "desc": [
                "조직이 위험을 관리하기 위해 내리는 의사결정 유형으로, 다음 네 가지 방법이 있습니다:",
                "1. 수용(Accept) – 위험을 감수하고 유지",
                "2. 회피(Avoid) – 위험의 원인을 제거",
                "3. 감소(Reduce) – 발생 가능성 또는 영향을 줄이는 조치",
                "4. 공유(Share) – 보험, 계약, 제휴 등을 통해 위험을 분산"
            ]
        },
        {
            "term": "ICI 위험 및 통제모델",
            "en": "ICI Risk and Control Model",
            "desc": [
                "ICI(국제내부통제협회)의 위험 및 통제모델은 3단계 구조(Three-Level Model) 로 구성되어 있습니다.",
                "세 단계는 환경통제(Environmental Controls), 시스템통제(System Controls), 거래처리통제(Transaction Processing Controls) 입니다.",
                "- 환경통제(Environmental Controls) 는 이사회(Board of Directors) 와 최고경영진(Executive Management) 의 책임으로, 시스템 및 거래처리 통제가 의도한 대로 수행될 수 있는 환경을 조성합니다.",
                "- 시스템통제(System Controls) 는 중간관리자(Middle Management) 의 책임으로, 예를 들어 급여(payroll) 등 특정 기능을 담당하는 부서에서 수행됩니다.",
                "- 거래처리통제(Transaction Processing Controls) 는 직원(Employee) 의 책임으로, 개별 거래 수준에서 통제를 수행합니다."
            ]
        },
        {
            "term": "위험분석",
            "en": "Risk Analysis",
            "desc": [
                "조직이 직면한 위험을 식별(Identify), 평가(Assess), 정량화(Quantify) 하여",
                "그 발생 가능성과 영향을 분석하는 과정입니다.",
                "위험분석은 특정 상황에서 성공 확률을 높이고 의사결정의 품질을 개선하기 위한 과정입니다."
            ]
        },
        {
            "term": "위험선호도",
            "en": "Risk Appetite",
            "desc": [
                "조직이 목표 달성을 위해 수용할 의사가 있는 위험 수준(Degree of Risk Willing to Accept) 을 의미합니다.",
                "이는 경영전략 수립 시, 위험과 수익 간 균형을 결정하는 핵심 기준으로 활용됩니다."
            ]
        },
        {
            "term": "위험평가",
            "en": "Risk Assessment",
            "desc": [
                "조직은 자신이 직면한 위험을 인식하고 이에 대응해야 합니다.",
                "이를 위해 판매, 생산, 마케팅, 재무 등 주요 활동과 통합된 목표(Objectives) 를 설정하고,",
                "그 목표 달성에 영향을 미칠 수 있는 위험을 식별·분석·관리하기 위한 메커니즘을 구축해야 합니다."
            ]
        },
        {
            "term": "위험허용도",
            "en": "Risk Tolerance",
            "desc": [
                "조직이 목표 달성 과정에서 성과 변동(Performance Variation) 을 수용할 수 있는 허용 범위를 의미합니다.",
                "즉, 위험선호도(Risk Appetite)를 구체화한 개념으로, 실제 운영 수준에서 허용 가능한 변동의 한계치를 정의합니다"
            ]
        },
        {
            "term": "위협",
            "en": "Threat",
            "desc": [
                "위협이란 특정한 상황에서 발생할 수 있으며, 조직에 손실(Loss) 을 초래할 수 있는 사건(Event) 또는 행위를 의미합니다.",
                "위협에는 내재적 위험(Inherent Hazards) 과 환경적 위험(Environmental Hazards) 이 모두 포함되며,",
                "이는 취약점(Vulnerability) 이 노출될 때 손실을 유발할 가능성이 있습니다."
            ]
        },
        {
            "term": "위협 포인트 매트릭스",
            "en": "Threat Point Matrix",
            "desc": [
                "통제 약점(Control Weakness) 으로 인해 손실 가능성이 높은 시스템 내 지점을 식별하기 위한 도구입니다.",
                "이 매트릭스는 다음과 같이 구성됩니다:",
                "- 한 축에는 통제 포인트(Control Points)",
                "- 다른 축에는 위협 포인트(Threat Points)",
                "해당 분야에 숙련된 전문가가 이 매트릭스를 활용할 경우, 손실 가능성이 높은 영역을 신뢰성 있게 식별할 수 있습니다."
            ]
        },
        {
            "term": "윤리강령",
            "en": "Code of Ethics",
            "desc": [
                "모든 인증전문가가 준수해야 할 윤리적 행동 기준을 명시한 문서입니다.",
                "해당 강령을 위반할 경우, 인증위원회(Certification Board) 에 의해 자격이 취소될 수 있습니다."
            ]
        },
        {
            "term": "윤리적 가치",
            "en": "Ethical Values",
            "desc": [
                "의사결정자가 적절한 행동 방향을 판단할 수 있도록 하는 도덕적 기준을 의미합니다.",
                "이러한 가치는 단순히 합법적인 행위에 그치지 않고, ‘옳은 것(What is Right)’ 에 근거해야 합니다."
            ]
        },
        {
            "term": "응용통제",
            "en": "Application Controls",
            "desc": [
                "응용 프로그램 내에 설정된 프로그램 절차(Programmed Procedures) 및 관련 수작업 절차로,",
                "정보 처리의 완전성(Completeness) 과 정확성(Accuracy) 을 보장하기 위해 설계된 통제입니다."
            ]
        },
        {
            "term": "이사회",
            "en": "Board of Directors",
            "desc": [
                "주주에 의해 선출되며, 기업의 전략적 목표를 수립하고 조직의 거버넌스(지배구조)를 감독하는 책임을 가진 기관입니다.",
                "기업의 형태에 따라 이사회(Board of Directors), 감독위원회(Supervisory Board),",
                "비영리기관의 경우 이사회(Board of Trustees), 정부기관의 경우 위원회(Board of Governors or Commissioners),",
                "파트너십의 경우 공동경영자(General Partners), 중소기업의 경우 사업주(Owner)가 그 역할을 수행할 수 있습니다.",
                "경영진은 이사회(또는 신탁이사회)에 대해 책임을 지며, 이사회는 조직의 건전한 경영, 윤리적 가치, 투명성 확보를 위한 감독과 지침 역할을 수행합니다.",
                "또한 경영진을 선임하고 그들의 업무를 모니터링함으로써 조직의 청렴성과 윤리 기준을 설정하고 관리하는 핵심 역할을 합니다."
            ]
        },
        {
            "term": "이해관계자",
            "en": "Stakeholders",
            "desc": [
                "조직의 활동에 직·간접적으로 영향을 받는 모든 주체를 의미합니다.",
                "예: 주주(Shareholders), 직원(Employees), 고객(Customers), 공급업체(Suppliers),",
                "그리고 조직이 운영되는 지역사회(Community) 등이 이에 해당합니다."
            ]
        },
        {
            "term": "이해상충",
            "en": "Conflicts of Interest",
            "desc": [
                "개인이 자신의 개인적·사회적·재정적·정치적 이익을 조직의 이익보다 우선시할 때 발생하는 상황을 말합니다.",
                "이해상충이 실제로 존재하지 않더라도 그렇게 보이는 것만으로도 개인과 조직의 명성이 훼손될 수 있습니다.",
                "잠재적인 이해상충은 즉시 상급 관리자에게 보고해야 하며,",
                "정기적으로 조직의 행동강령 또는 윤리기준 준수 확인 절차에서도 이를 명확히 공개해야 합니다.",
                "대부분의 이해상충 문제는 단순한 조정과 상호 합의를 통해 해결이 가능합니다."
            ]
        },
        {
            "term": "1974년 개인정보보호법",
            "en": "Privacy Act of 1974",
            "desc": [
                "미국 연방법으로, 개인정보를 포함하는 데이터의 오용(Misuse) 또는 침해(Compromise) 를 방지하기 위해",
                "연방기관(Federal Agencies) 에 다양한 요구사항을 부과합니다.",
                "특히 자동자료처리기관(ADP Organizations) 은 개인 데이터를 처리할 때,",
                "무단 공개, 파기, 변경으로부터 데이터를 보호하기 위한 합리적인 수준의 보안조치를 취해야 합니다.",
                "이 법은 고의적 행위뿐만 아니라 사고나 부주의로 인한 데이터 유출에도 적용됩니다."
            ]
        },
        {
            "term": "GAAP, 일반적으로 인정된 회계원칙",
            "en": "Generally Accepted Accounting Principles",
            "desc": [
                "미국 재무회계기준위원회(FASB, Financial Accounting Standards Board) 가 제정한 회계 지침과 기준으로,",
                "미국 내에서 회계처리와 재무보고를 수행할 때 적용되는 주요 회계 규범집(Main U.S. Accounting Rule Book) 을 의미합니다.",
                "즉, 기업이 재무제표를 작성할 때 따라야 하는 표준화된 회계원칙 체계입니다."
            ]
        }
    ],
    "ㅈ": [
        {
            "term": "자금조달 사이클",
            "en": "Financing Cycle",
            "desc": [
                "기업의 자본금 발행 및 상환, 배당금 지급, 리스 등 자금조달 수단의 검토 및 선택,",
                "채무 관리(계약이행 및 약정조건 준수 모니터링), 투자관리 및 유가증권의 보관관리 등",
                "자금의 조달과 운용에 관련된 일련의 활동을 포함합니다."
            ]
        },
        {
            "term": "자동 통제",
            "en": "Automated Controls",
            "desc": [
                "기술을 통해 대부분 또는 전부 수행되는 통제활동을 말합니다.",
                "예를 들어, 컴퓨터 소프트웨어에 프로그래밍된 자동화된 통제 기능 등이 이에 해당합니다.",
                "이는 **수작업 통제(Manual Controls)**와 대비되는 개념으로, 시스템에 내장된 절차를 통해 정확성과 효율성을 향상시키는 통제 방식입니다."
            ]
        },
        {
            "term": "자산 보호",
            "en": "Asset Safeguarding",
            "desc": [
                "조직이 보유하거나 취득한 자산이 도난, 남용 등으로부터 충분히 보호될 수 있도록 마련한 절차 및 관리활동을 말합니다."
            ]
        },
        {
            "term": "자산 접근 권한",
            "en": "Access to Assets",
            "desc": [
                "자산에 대한 접근은 반드시 경영진의 승인과 권한 부여에 따라 허용되어야 합니다."
            ]
        },
        {
            "term": "자산 책임성",
            "en": "Asset Accountability",
            "desc": [
                "기록된 자산 내역과 실제 자산을 합리적인 주기로 비교하고, 불일치가 발견될 경우 이에 대해 적절한 조치를 취하는 과정을 의미합니다."
            ]
        },
        {
            "term": "잔여위험",
            "en": "Residual Risk",
            "desc": [
                "경영진이 위험에 대한 대응조치(Risk Response) 를 설계하고 실행한 이후에도",
                "여전히 남아 있는 목표 달성 저해 가능성을 의미합니다.",
                "즉, 위험을 완전히 제거할 수 없기 때문에, 대응 이후에도 존재하는 불가피한 위험 수준입니다."
            ]
        },
        {
            "term": "재무담당 임원",
            "en": "Financial Officers",
            "desc": [
                "재무 및 관리회계 담당 임원과 그 조직은 기업의 모든 운영 단위를 수평적·수직적으로 관통하는 핵심 기능을 수행합니다.",
                "이들은 조직 전반의 예산 및 계획 수립, 성과 분석, 운영·재무·준법 측면에서의 관리활동을 수행합니다.",
                "따라서 최고재무책임자(CFO), 최고회계책임자(CAO), Controller(관리회계책임자) 등은",
                "경영진이 통제를 실행하는 데 있어 핵심적인 역할을 담당합니다."
            ]
        },
        {
            "term": "재무위원회",
            "en": "Finance Committee",
            "desc": [
                "이사회 산하의 위원회로, 대규모 자금 집행 및 자본지출 예산의 운영계획과의 일치 여부를 검토·통제하는 역할을 합니다.",
                "즉, 조직의 재정적 의사결정이 경영전략과 일관되도록 관리하는 기능을 수행합니다."
            ]
        },
        {
            "term": "재무제표",
            "en": "Financial Statements",
            "desc": [
                "일반적으로 다음의 항목으로 구성됩니다:",
                "1. 재무상태표(Statement of Financial Position)",
                "2. 손익계산서(Statement of Income)",
                "3. 자본변동표(Statement of Changes in Equity)",
                "4. 현금흐름표(Statement of Cash Flows)",
                "5. 주석(Notes to the Financial Statements)",
                "이들은 조직의 재무성과와 재무상태를 이해하기 위한 기본 보고서입니다."
            ]
        },
        {
            "term": "전략기획",
            "en": "Strategic Planning",
            "desc": [
                "조직의 장기적 목표(Long-term Objectives) 와 사업 방향성(Business Direction) 을 결정하는",
                "고차원적 계획수립 과정입니다.",
                "이는 진출 또는 철수할 사업영역 결정, 생산능력 계획(Capacity Planning),",
                "인력계획(Long-range Workforce Planning) 등 조직의 미래지향적 의사결정을 포함합니다."
            ]
        },
        {
            "term": "전사적 / 조직 전체적",
            "en": "Entity-wide",
            "desc": [
                "조직 전체에 적용되는 활동을 의미하며, 주로 전사적 통제(Entity-wide Controls) 와 관련됩니다."
            ]
        },
        {
            "term": "ERM, 전사적 위험관리",
            "en": "Enterprise Risk Management",
            "desc": [
                "전사적 위험관리(ERM)는 경영진의 기업 운영 방식에서 파생된 8개의 상호 연관된 구성요소로 이루어져 있습니다.",
                "이 구성요소는 경영 프로세스 전반에 통합되어 있으며 다음과 같습니다:",
                "1. 내부환경(Internal Environment)",
                "2. 목표설정(Objective Setting)",
                "3. 사건식별(Event Identification)",
                "4. 위험평가(Risk Assessment)",
                "5. 위험대응(Risk Response)",
                "6. 통제활동(Control Activities)",
                "7. 정보 및 의사소통(Information and Communication)",
                "8. 모니터링(Monitoring)",
                "ERM은 조직의 사명과 목표 달성을 위한 통합적 위험관리 체계를 제공합니다."
            ]
        },
        {
            "term": "전술계획",
            "en": "Tactical Planning",
            "desc": [
                "전술계획은 전략계획(Strategic Planning) 을 실행하기 위한 단기적(Short-term) 관리활동 및 실행계획을 의미합니다.",
                "이는 조직의 전략목표를 구체적인 운영활동 요구사항(Operational Requirements) 으로 전환하는 과정입니다.",
                "주로 연간 예산수립(Yearly Budgeting), 성과 모니터링(Performance Monitoring) 등과 같은",
                "단기계획 및 통제 사이클을 포함하며, 경영 기반의 통제시스템 중 가장 강력한 형태 중 하나로 평가됩니다."
            ]
        },
        {
            "term": "EDI, 전자자료교환",
            "en": "Electronic Data Interchange",
            "desc": [
                "종이 문서를 완전히 배제하고, 고객과 공급자의 컴퓨터 간에 직접 통신을 통해 일상적인 거래를 자동화하는 시스템입니다.",
                "EDI는 문서 흐름을 단순화하고 거래 속도와 정확성을 높입니다."
            ]
        },
        {
            "term": "EFT, 전자자금이체",
            "en": "Electronic Funds Transfer",
            "desc": [
                "종이 문서가 아닌 전자통신(Electronic Communication) 을 통해 자금을 이체하는 시스템입니다.",
                "예: 인터넷뱅킹, 자동이체, 전자결제 등"
            ]
        },
        {
            "term": "절차",
            "en": "Procedure",
            "desc": [
                "정책(Policy)을 실행하기 위한 구체적인 행동(Activity) 또는 단계적 수행 과정을 의미합니다.",
                "즉, 정책이 “무엇을 해야 하는가”를 정의한다면, 절차는 “어떻게 해야 하는가(How to do it)”를 구체화합니다."
            ]
        },
        {
            "term": "접근 위협",
            "en": "Access Threats",
            "desc": [
                "허가받지 않은 접근 또는 부적절한 처리가 발생할 위험을 말합니다.",
                "외부 요인에 의한 위험은 일반적으로 보안 위험(Security Risks) 또는 접근성 위험(Accessibility Risks) 이라고 부릅니다.",
                "‘허가되지 않은 행위(Unauthorized Acts)’란 경영진으로부터 정식 권한을 부여받지 않은 사람이나 프로그램이 수행하는 행위를 의미합니다."
            ]
        },
        {
            "term": "정보 및 의사소통",
            "en": "Information and Communication",
            "desc": [
                "정보 및 의사소통 시스템은 조직의 사람들로 하여금 업무 수행, 관리, 통제에 필요한 정보를 수집하고 교환할 수 있도록 지원하는 활동을 의미합니다.",
                "이 시스템은 내부통제의 다른 구성요소를 연결하는 핵심 기반요소입니다."
            ]
        },
        {
            "term": "정보자유법",
            "en": "Freedom of Information Act",
            "desc": [
                "미국 연방법으로, 연방기관(Federal Agencies) 이 개인 관련 정보를 포함한 특정 정보를",
                "연방관보(Federal Register) 에 공개하도록 규정합니다.",
                "또한, 기관이 보유한 정보의 출처(Source) 와 획득방법(Method) 도 공개되어야 하며,",
                "국민이 정부가 보유한 정보에 접근할 수 있는 투명성과 접근권(Right to Know) 을 보장합니다."
            ]
        },
        {
            "term": "정직성 / 진실성",
            "en": "Integrity",
            "desc": [
                "도덕적 원칙에 입각한 올바름, 성실함, 진정성을 의미합니다.",
                "정직성은 ‘옳은 일을 하고자 하는 의지’이며, 조직이 추구하는 가치와 기대 기준을 스스로 지키려는 태도를 포함합니다."
            ]
        },
        {
            "term": "정책",
            "en": "Policy",
            "desc": [
                "경영진 또는 이사회가 내부통제 구현을 위해 해야 할 일(What should be done) 을 명확히 제시한 선언적 문서 또는 방침입니다.",
                "정책은 문서화되어 있거나 공식 커뮤니케이션을 통해 명시될 수도 있으며, 때로는 경영진의 행동이나 의사결정을 통해 암묵적으로 표현되기도 합니다.",
                "정책(Policy) 은 구체적인 실행 절차인 절차(Procedure) 의 기반이 됩니다."
            ]
        },
        {
            "term": "조직",
            "en": "Organization",
            "desc": [
                "조직은 이사회(Board of Directors), 경영진(Senior Management), 그리고 그 외 임직원 전체로 구성된 사람들의 집합체를 의미합니다.",
                "즉, 조직의 목표 달성과 내부통제 수행에 관여하는 모든 인적 요소를 포함합니다."
            ]
        },
        {
            "term": "조직 수준 / 개별 단위 상위 수준",
            "en": "Entity-level",
            "desc": [
                "조직 전체 수준에서 이루어지는 활동으로, 자회사·사업부·운영단위·기능 등 개별 단위와 구분되는 상위 수준의 통제나 활동을 의미합니다."
            ]
        },
        {
            "term": "조직도",
            "en": "Organizational Chart",
            "desc": [
                "조직 내 부서, 사업부, 관리자의 역할과 책임 관계를 도식적으로 나타낸 구조도입니다.",
                "즉, 부문 간 관계와 보고체계(Reporting Line) 를 시각적으로 표현하여 조직의 운영 구조를 명확히 보여줍니다."
            ]
        },
        {
            "term": "조직체 / 법인체",
            "en": "Entity",
            "desc": [
                "특정 목적을 위해 설립된 법적 실체 또는 관리 운영 모델을 의미합니다.",
                "예를 들어, 영리기업, 비영리단체, 정부기관, 교육기관 등이 이에 해당합니다.",
                "관리운영 모델은 제품·서비스 라인, 사업부(Division), 운영단위(Unit) 등의 형태로 구성될 수 있으며,",
                "지리적 구분에 따라 더 세분화되거나 통합될 수도 있습니다."
            ]
        },
        {
            "term": "존재 및 작동",
            "en": "Present and Functioning",
            "desc": [
                "내부통제의 구성요소와 원칙에 적용되는 개념으로,",
                "- “존재(Present)” 는 내부통제 구성요소와 관련 원칙이 시스템의 설계 및 실행 단계에서 실제로 존재함을 확인하는 것을 의미합니다.",
                "- “작동(Functioning)” 은 내부통제 구성요소와 원칙이 운영 과정에서도 지속적으로 유지되고 제대로 작동함을 확인하는 것을 뜻합니다.",
                "즉, 내부통제는 단순히 설계상 존재할 뿐 아니라, 실제 운영 중에도 기능하고 있어야 효과적이라고 할 수 있습니다."
            ]
        },
        {
            "term": "준법",
            "en": "Compliance",
            "desc": [
                "조직에 적용되는 법률 및 규정을 준수하는 상태 또는 그 과정을 의미합니다."
            ]
        },
        {
            "term": "준법 헬프라인 / 윤리신고센터",
            "en": "Compliance Hotline",
            "desc": [
                "현장에서 해결되지 않는 준법 관련 문제를 익명으로 제보하거나 상담할 수 있는 신고채널입니다.",
                "대부분의 경우 연중무휴(24시간·365일) 운영되며, 조직의 윤리·법규 위반 사항을 신고하거나",
                "정보 및 조언을 받을 수 있는 수단을 제공합니다."
            ]
        },
        {
            "term": "중대한 결함",
            "en": "Major Deficiency",
            "desc": [
                "조직이 목표를 달성할 가능성을 심각하게 저해하는 내부통제의 결함 또는 결함들의 결합을 의미합니다.",
                "이는 내부통제 시스템의 효과성에 중대한 영향을 미치는 수준의 약점을 지칭합니다."
            ]
        },
        {
            "term": "중요성",
            "en": "Materiality",
            "desc": [
                "통제를 결정하는 주요 기준으로, 거래의 금액적 중요성 또는 상대적 가치를 의미합니다.",
                "중요성은 보호 대상 사건의 가치가 조직 전체 가치에서 차지하는 비율에 따라 달라집니다.",
                "즉, 가치가 클수록 강력한 통제가 필요하며,",
                "통제 강도와 보호가치 간에는 높은 상관관계가 존재해야 합니다."
            ]
        },
        {
            "term": "중요성 개념",
            "en": "Materiality Concept",
            "desc": [
                "기업은 재무상태에 중대한 영향을 미치는 항목에 대해서만",
                "엄격한 회계처리를 수행해야 한다는 원칙입니다.",
                "즉, 중요하지 않은 항목은 실무 효율성을 위해 단순화할 수 있습니다."
            ]
        },
        {
            "term": "지속적 전문교육",
            "en": "Continuing Professional Education",
            "desc": [
                "CICS 자격 보유자는 자격 유지를 위해 3년마다 총 120시간의 지속적 전문교육(CPE, Continuing Professional Education) 을 이수하거나 재인증 시험을 통과해야 합니다.",
                "CPE는 전문 학회 참석, 교육·훈련 과정 수강, 교육 개발 및 강의, 전문지식 공유, 논문 발표,",
                "전문위원회 또는 특수 관심 그룹 활동 참여 등을 통해 이수할 수 있습니다."
            ]
        },
        {
            "term": "지출사이클",
            "en": "Expenditures Cycle",
            "desc": [
                "지출사이클은 구매(Purchasing), 급여(Payroll), 지급(Disbursement) 의 세 가지 기능으로 구분됩니다.",
                "즉, 조직의 비용이 발생하고 지급되는 전 과정을 포괄하는 회계주기입니다."
            ]
        },
        {
            "term": "직무분리",
            "en": "Segregation of Duties",
            "desc": [
                "직원 간의 업무와 책임을 분리(Segregation) 하여 서로의 작업이 자동적으로 검증되도록 하는 내부통제 체계입니다.",
                "이는 견제와 균형(Check and Balance) 을 통해 오류 및 부정을 예방하는 핵심 통제 원칙입니다.",
                "직무분리는 다음 네 가지 유형으로 구성됩니다:",
                "1. 운영(Operation) 과 기록관리(Recordkeeping) 의 분리",
                "2. 자산보관(Custody) 과 회계(Accounting) 의 분리",
                "3. 자산보관(Custody) 과 승인(Authorization) 의 분리",
                "4. 회계업무 간 상호 분리(Accounting Tasks Segregation)",
                "이러한 구조를 통해 자산의 오용이나 오류를 방지하고, 내부통제 환경의 신뢰성을 확보할 수 있습니다."
            ]
        },
        {
            "term": "직무책임 분리 충돌 매트릭스",
            "en": "Segregation of Responsibilities Conflict Matrix",
            "desc": [
                "전통적인 직무분리 개념은 전산화된 환경(Computerized Business Applications) 에서 항상 효과적이지 않을 수 있습니다.",
                "따라서 정보시스템 환경에서는 데이터 단위 수준(Data Element Level) 에서 접근권한을 제한하는 방식으로 직무분리를 구현할 수 있습니다.",
                "이를 위해 충돌 매트릭스(Conflict Matrix) 를 활용하여,",
                "- 분리되어야 할 기능(Function)을 식별하고",
                "- 현재의 분리 수준이 충분한지를 평가(Assessment)합니다.",
                "즉, 전산 시스템 내 세분화된 권한관리(Granular Access Control) 를 통해 내부통제의 효과성을 확보합니다."
            ]
        },
        {
            "term": "집중형 조직",
            "en": "Centralized Company",
            "desc": [
                "주요한 계획, 지휘, 통제 결정이 최고경영진에 의해 이루어지는 기업을 말합니다."
            ]
        }
    ],
    "ㅊ": [
        {
            "term": "CEO, 최고경영자",
            "en": "Chief Executive Officer",
            "desc": [
                "기업의 단기 및 장기 계획을 실행할 최고 책임자로, 조직 전체의 내부통제 시스템에 대한 최종 책임을 집니다.",
                "CEO는 Sarbanes–Oxley Act(사베인즈-옥슬리법) 에 따라 내부통제 시스템의 적정성을 공식적으로 증명해야 합니다."
            ]
        },
        {
            "term": "최고경영층의 태도",
            "en": "Tone at the Top",
            "desc": [
                "“Tone at the Top”은 조직문화와 윤리의 방향성을 최고경영층이 설정하는 것을 의미합니다.",
                "이는 단순히 지침을 내리는 것을 넘어, 경영진이 직접 모범을 보이는 행동(‘Walking the Talk’) 을 통해",
                "직원들에게 올바른 가치와 행동 기준을 전달해야 함을 뜻합니다.",
                "즉, 경영진의 윤리적 태도와 행동은 조직 전반의 내부통제 문화 형성에 결정적인 영향을 미칩니다."
            ]
        },
        {
            "term": "COO, 최고운영책임자",
            "en": "Chief Operations Officer",
            "desc": [
                "조직의 품질관리(Quality Control) 및 통계적 공정관리(Statistical Process Control) 를 총괄하며,",
                "업무 프로세스 전반과 생산품의 품질에 대한 통제 책임을 집니다."
            ]
        },
        {
            "term": "CFO, 최고재무책임자",
            "en": "Chief Financial Officer",
            "desc": [
                "조직의 내부회계통제(Internal Accounting Controls) 에 대한 주요 책임을 가지며,",
                "재무기록, 주주보고서, 성과보고서 등 재무 관련 시스템의 관리 및 통제를 담당합니다."
            ]
        },
        {
            "term": "추천서",
            "en": "Reference Letter",
            "desc": [
                "내부통제 설계 및 평가 업무 경험을 증명할 수 있는 인사가 작성한 공식 서신입니다.",
                "추천서에는 반드시 직무 경험(Job Experience) 에 대한 구체적인 설명이 포함되어야 합니다."
            ]
        },
        {
            "term": "출력처리 통제",
            "en": "Output Processing Controls",
            "desc": [
                "컴퓨터 처리 과정이 완료된 후부터 사용자에게 데이터가 전달되기까지의 출력 데이터 무결성(Integrity) 을 보장하기 위한 통제입니다.",
                "사용자는 정확하고 완전한 데이터가 적시에 제공되어야 일상 업무를 수행할 수 있으므로, 출력통제는 전산기록 시스템의 핵심 통제 영역 중 하나입니다.",
                "출력통제의 주요 범위는 다음과 같습니다:",
                "- 정보기술 균형 및 대사(IT Balancing & Reconciliation)",
                "- 출력물 배포 관리(Output Distribution)",
                "- 사용자 검증 및 대사(User Balancing & Reconciliation)",
                "- 기록 보존(Record Retention)",
                "- 책임문서 관리(Accountable Document Control)",
                "- 출력오류 처리(Output Error Handling)",
                "즉, 출력통제의 목적은 승인된(Authorized), 완전한(Complete), 정확한(Accurate) 정보가 사용자에게 전달되도록 보장하는 것입니다."
            ]
        },
        {
            "term": "취약점",
            "en": "Vulnerability",
            "desc": [
                "취약점은 비즈니스 시스템(프로세스)의 흐름 내에 존재하는 약점(Weakness) 으로,",
                "조직이 손실(Loss) 에 노출될 가능성을 높이는 요인을 의미합니다.",
                "예를 들어, 컴퓨터 단말기에 암호를 메모지에 붙여두는 경우,",
                "무단 접근 권한이 없는 사용자(Unauthorized User) 가 이를 악용해",
                "조직에 피해를 초래할 수 있습니다.",
                "즉, 취약점은 위협(Threat) 이 현실화될 수 있는 통제상의 빈틈(Control Gap) 으로,",
                "조직의 자산·정보·운영에 부정적인 영향을 미칠 수 있는 요소입니다."
            ]
        }
    ],
    "ㅋ": [
        {
            "term": "카테고리 / 통제목표 범주",
            "en": "Category",
            "desc": [
                "내부통제의 목적(Objectives)을 세 가지 범주로 구분한 것입니다.",
                "이 범주는 운영(Operations), 보고(Reporting), 준법(Compliance) 과 관련됩니다."
            ]
        },
        {
            "term": "컴퓨터 처리 통제",
            "en": "Computer Processing Controls",
            "desc": [
                "컴퓨터 처리 과정에서 데이터의 정확성과 완전성을 보장하기 위해 사용되는 통제활동입니다.",
                "이 통제는 데이터가 시스템에 입력된 이후, 응용프로그램이 데이터를 처리하는 동안 적용되며, 프로그램 간 인터페이스, 파일 간 연계, 오류 처리 절차 등을 포함합니다.",
                "또한, 응용프로그램마다 고유하게 설계되어 있어 다른 시스템 간에는 그대로 이전(Transfer)되지 않을 수도 있습니다.",
                "컴퓨터 처리 통제를 개발·유지하는 과정에서는 내부통제의 기본 원칙(예: 직무분리) 이 전산처리 기능에도 지속적으로 반영되도록 해야 합니다."
            ]
        },
        {
            "term": "코소",
            "en": "COSO",
            "desc": [
                "Committee of Sponsoring Organizations of the Treadway Commission 의 약칭으로,",
                "내부통제 평가와 기업위험관리(ERM)에 관한 지침을 제공하기 위해 설립된 위원회입니다.",
                "COSO는 다음 다섯 기관으로 구성됩니다:",
                "- 미국재무임원협회(FEI)",
                "- 미국공인회계사협회(AICPA)",
                "- 미국회계학회(AAA)",
                "- 내부감사인협회(IIA)",
                "- 경영회계사협회(IMA)",
                "COSO는 기업위험관리(ERM), 내부통제, 부정방지(Fraud Deterrence) 분야에서",
                "국제적 프레임워크와 가이드라인을 개발하는 리더십 기관입니다."
            ]
        },
        {
            "term": "클라우드 컴퓨팅",
            "en": "Cloud Computing",
            "desc": [
                "소프트웨어와 데이터를 기업 내부가 아닌 제3자의 서버(Third-Party Server) 에 저장하고,",
                "직원이 인터넷을 통해 접근하여 사용하는 컴퓨팅 방식입니다."
            ]
        }
    ],
    "ㅌ": [
        {
            "term": "탐지 통제",
            "en": "Detective Control",
            "desc": [
                "초기 처리 이후에 발생한 의도치 않은 사건이나 결과를 발견하기 위해 설계된 통제입니다.",
                "설계 목적에 따라 통제는 일반적으로 예방통제(Preventive Control) 와 탐지통제(Detective Control) 로 구분되며,",
                "탐지통제는 문제가 발생한 후 목표 달성 전에 오류나 이상을 식별하여 수정 조치를 취할 수 있게 합니다."
            ]
        },
        {
            "term": "통제",
            "en": "Control",
            "desc": [
                "목표 달성 과정에서 발생할 수 있는 위험을 최소화하거나 제거하기 위한 모든 방법·기법·절차를 의미합니다.",
                "명사로서(Control): 내부통제의 일부로 존재하는 정책 또는 절차를 의미하며, 이는 5대 구성요소 각각에 내재되어 있습니다.",
                "동사로서(to control): 정책이나 절차를 수립·실행하여 내부통제 원칙을 구현하는 행위를 뜻합니다."
            ]
        },
        {
            "term": "통제계정",
            "en": "Control Account",
            "desc": [
                "보조원장에서 관리되는 관련 계정들의 잔액 합계와 일치하는 잔액을 가진 주계정을 말합니다."
            ]
        },
        {
            "term": "통제계층구조",
            "en": "Control Hierarchy",
            "desc": [
                "내부통제를 계층적으로 분류한 체계를 의미합니다.",
                "가장 상위에는 환경통제(Environmental Controls) 가 있으며,",
                "그 아래에 시스템통제(System Controls), 그 아래 단계에는 거래처리통제(Transaction Processing Controls) 가 위치합니다.",
                "상위 통제는 하위 통제의 운영과 효과성에 영향을 미칩니다."
            ]
        },
        {
            "term": "통제목표",
            "en": "Control Objectives",
            "desc": [
                "위험에 대한 긍정적 진술(Positive Statement) 로, 내부통제의 기대 결과를 정의합니다.",
                "예를 들어, “거래가 누락될 위험이 있다”는 위험에 대응하는 통제목표는",
                "“모든 거래가 정확히 처리된다”가 될 수 있습니다.",
                "통제목표는 회계문헌, 미국 해외부패방지법(FCPA), 사베인즈-옥슬리법(SOX) 등에 언급된 일반적인 시스템 통제목표를 포함합니다."
            ]
        },
        {
            "term": "통제시스템",
            "en": "System of Control",
            "desc": [
                "여러 개별 통제(Individual Controls)로 구성된 통합적 통제체계(Integrated System of Controls) 를 의미합니다.",
                "각 개별 통제는 특정 시점에서 효과적일 수 있지만,",
                "전체적인 통제시스템(System as a Whole) 이 갖추어져야 프로세스 전반이 통제되고 있음을 보장할 수 있습니다.",
                "예를 들어, 우편실에서 수표를 통제하더라도,",
                "은행 입금까지의 전 과정이 통합된 시스템으로 관리되지 않으면 현금수입의 완전한 통제는 불가능합니다.",
                "따라서 중요한 것은 단일 통제가 아닌 전체 통제체계(Systemic Control) 의 완전성과 연계성입니다."
            ]
        },
        {
            "term": "통제위협",
            "en": "Control Threats",
            "desc": [
                "데이터의 정확성과 완전성, 그리고 응용시스템의 운영 성능을 위협하는 요소를 말합니다.",
                "이는 시스템 내 데이터의 무결성, 통제의 효과성, 경제성, 효율성에 영향을 미칠 수 있는 위험을 포함합니다."
            ]
        },
        {
            "term": "통제지점",
            "en": "Control Point",
            "desc": [
                "응용시스템 내에서 손실 가능성이 가장 크고, 통제가 설치되어야 하는 핵심 지점을 의미합니다."
            ]
        },
        {
            "term": "통제환경",
            "en": "Control Environment",
            "desc": [
                "조직의 핵심은 사람이며, 그들의 정직성, 윤리적 가치, 역량과 이를 둘러싼 조직문화와 경영환경이 내부통제의 근간을 이룹니다.",
                "통제환경은 조직의 통제 의식(Control Consciousness) 을 형성하고,",
                "모든 내부통제 구성요소의 기반으로 규율(Discipline) 과 구조(Structure) 를 제공합니다.",
                "주요 요소에는 조직 구성원의 정직성과 윤리 가치, 경영진의 철학과 운영 방식, 권한과 책임의 부여 방식, 인사 관리, 이사회의 감독 및 방향 제시 등이 포함됩니다."
            ]
        },
        {
            "term": "통제활동",
            "en": "Control Activities",
            "desc": [
                "경영진의 지시사항이 제대로 수행되도록 보장하는 정책과 절차입니다.",
                "조직 전체의 모든 수준과 기능에서 이루어지며,",
                "승인(Approvals), 권한부여(Authorizations), 검증(Verifications), 대사(Reconciliations),",
                "운영성과 검토(Review of Performance), 자산보호(Security of Assets), 직무분리(Segregation of Duties) 등 다양한 활동을 포함합니다."
            ]
        },
        {
            "term": "통합 작동 / 상호운영",
            "en": "Operating Together",
            "desc": [
                "내부통제의 다섯 가지 구성요소가 상호 연계되어 조직의 목표 달성 실패 위험을 허용 가능한 수준으로 낮추는 상태를 의미합니다.",
                "즉, 각 구성요소가 개별적으로 기능할 뿐만 아니라, 통합적으로 작동하여 전체 내부통제 시스템의 효과성을 확보하는 것을 뜻합니다."
            ]
        }
    ],
    "ㅍ": [
        {
            "term": "평가 프로그램",
            "en": "Assessment Program",
            "desc": [
                "평가 프로그램이란 **시스템이나 활동을 평가하기 위한 절차(Procedure)**를 말합니다.",
                "이는 평가자가 평가를 수행할 때 따라야 하는 **일련의 단계(Steps)**로 구성됩니다.",
                "평가 프로그램은 목표(Objectives) 와 그 목표 달성을 방해할 수 있는 위험(Risks) 을 중심으로 설계되며,",
                "평가 과정에서는 증거(Evidence) 를 수집·기록·검토하는 절차가 반드시 포함됩니다."
            ]
        },
        {
            "term": "프로세스 엔지니어링 사이클",
            "en": "Process Engineering Cycle",
            "desc": [
                "개별 시스템이나 응용프로그램을 구축(Build) 하는 데 포함된 일련의 활동을 의미합니다.",
                "이 사이클 동안 조직은 위험을 식별하고(Risk Identification), 평가하며(Measurement), 통제수단을 정의·적용(Control Implementation) 합니다.",
                "또한, 업무운영의 효율성(Efficiency) 과 효과성(Effectiveness) 을 평가하고,",
                "현행 비즈니스 요구사항이나 규제요건(Regulatory Requirements) 에 대응하기 위해 시스템을 변경(Change) 또는 업그레이드(Upgrade) 하는 활동도 포함됩니다."
            ]
        },
        {
            "term": "플레이 스크립트 / 절차 대본 기법",
            "en": "Play Script",
            "desc": [
                "플레이 스크립트는 절차를 단계별로 명확하게 정의하는 문서화 방식으로, 연극 대본(script)에서 유래된 개념입니다.",
                "각 단계를 따라가기 쉽게 서술함으로써, 업무 절차를 표준화하고 일관성 있게 수행할 수 있도록 돕습니다."
            ]
        },
        {
            "term": "PDCA 사이클",
            "en": "PDCA Cycle",
            "desc": [
                "모든 품질경영(Quality Management) 프로그램의 핵심 전제는 지속적인 개선(Continuous Improvement) 에 대한 강조입니다.",
                "이 지속적 개선 접근법은 PDCA 사이클(Plan-Do-Check-Act) 로 표현되며,",
                "1930년대 벨시스템(Bell System) 의 쉐워트 박사(Dr. Shewhart) 에 의해 개발되었습니다.",
                "PDCA 사이클은 계획(Plan) → 실행(Do) → 점검(Check) → 조치(Act) 의 네 단계를 포함하며,",
                "‘데밍 휠(Deming Wheel)’ 로도 불리며 품질관리의 핵심 개념 중 하나로 인정받습니다."
            ]
        }
    ],
    "ㅎ": [
        {
            "term": "합리적 보증",
            "en": "Reasonable Assurance",
            "desc": [
                "내부통제는 아무리 잘 설계되고 운영되더라도, 조직의 목표 달성을 절대적으로 보장할 수는 없다는 개념을 의미합니다.",
                "이는 모든 내부통제 시스템이 가지는 내재적 한계(Inherent Limitations) 때문입니다."
            ]
        },
        {
            "term": "FCPA, 해외부패방지법",
            "en": "Foreign Corrupt Practices Act",
            "desc": [
                "1977년에 제정된 미국 연방법으로, 부적절한 내부회계통제(Inadequate Internal Accounting Control) 를",
                "연방 범죄(Federal Offense) 로 규정하였습니다.",
                "이 법은 기업이 부패행위 및 부정회계를 방지하기 위해 ‘적정 수준의 내부회계통제(Adequate Internal Controls)’ 를 유지하도록 요구합니다."
            ]
        },
        {
            "term": "KPI, 핵심성과지표",
            "en": "Key Performance Indicator",
            "desc": [
                "조직이나 부서가 목표를 얼마나 효과적으로 달성하고 있는지를 측정하는 핵심 성과 지표를 의미합니다.",
                "KPI는 경영진이 조직의 성과를 평가하고 전략적 목표 달성 여부를 판단하는 데 활용되는 요약된 성과 측정지표(Summary Performance Measure) 입니다."
            ]
        },
        {
            "term": "행동강령 정책",
            "en": "Code of Conduct Policy",
            "desc": [
                "조직의 직원 행동 기준과 윤리 원칙(Code of Ethics) 을 명문화한 정책으로,",
                "직원이 업무를 수행함에 있어 따라야 할 기본 가치와 행동 지침을 규정합니다."
            ]
        },
        {
            "term": "환경적 위험",
            "en": "Environmental Risk",
            "desc": [
                "조직의 내부 및 외부 환경 요인과 관련된 위험을 의미합니다.",
                "예: 시장변화, 규제환경, 사회적 책임, 내부 조직문화 등"
            ]
        },
        {
            "term": "환경통제",
            "en": "Environmental Controls",
            "desc": [
                "환경통제는 경영진의 책임 하에 설정되며, 조직 운영 방식과 가치관을 반영합니다.",
                "즉, 경영진이 설정한 가치와 업무 수행 방식이 조직의 환경(Environment)을 형성하고,",
                "이 환경이 조직의 업무 수행 방식과 운영 전반에 중대한 영향을 미칩니다.",
                "따라서 환경통제는 조직의 행동기준과 성과문화에 직접적인 영향을 미치는 핵심 통제입니다."
            ]
        },
        {
            "term": "활동",
            "en": "Activity",
            "desc": [
                "COSO에서는 ‘활동(Activity)’이라는 용어를 급여(payroll) 등과 같은 **운영 단위(Operational Unit)**로 정의합니다.",
                "여기에는 시스템 통제(System Controls) 와 거래처리 통제(Transaction Processing Controls) 가 모두 포함됩니다."
            ]
        },
        {
            "term": "회계",
            "en": "Accounting",
            "desc": [
                "회계란 기업의 경영활동을 측정하고, 그 정보를 보고서 형태로 처리하여 의사결정자에게 전달하는 정보시스템을 말합니다."
            ]
        },
        {
            "term": "회계 사이클 / 회계순환과정",
            "en": "Accounting Cycle",
            "desc": [
                "기업이 일정 기간 동안의 재무제표를 작성하기 위해 수행하는 일련의 회계 절차를 의미합니다."
            ]
        },
        {
            "term": "AIS, 회계정보시스템",
            "en": "Accounting Information System",
            "desc": [
                "의사결정에 유용한 정보를 제공하기 위해 회계 데이터를 수집·기록·저장·처리하는 시스템입니다."
            ]
        },
        {
            "term": "효과적인 내부통제",
            "en": "Effective Internal Control",
            "desc": [
                "효과적인 내부통제 시스템은 조직이 목표를 달성할 수 있도록 합리적 보증(reasonable assurance) 을 제공합니다.",
                "이를 위해 내부통제의 다섯 가지 구성요소와 관련 원칙이 존재하고 제대로 기능해야 하며,",
                "이 다섯 구성요소가 상호 연계되어 일관되게 작동해야 합니다."
            ]
        },
        {
            "term": "후보추천위원회",
            "en": "Nominating Committee",
            "desc": [
                "이사회 산하 위원회로, 이사 후보자 및 최고경영진(Top Management) 후보자의 선임 과정을 관리·통제하는 역할을 합니다.",
                "조직의 지배구조(Governance) 투명성과 독립성을 확보하기 위한 핵심적인 내부통제 기능 중 하나입니다."
            ]
        },
        {
            "term": "COSO, 후원단체위원회",
            "en": "Committee of Sponsoring Organizations",
            "desc": [
                "기업위험관리(ERM), 내부통제(Internal Control) 및 부정방지(Fraud Deterrence) 에 관한",
                "사고리더십(Thought Leadership)을 제공하는 국제위원회입니다.",
                "COSO는 내부통제의 표준 프레임워크인 ‘COSO Internal Control – Integrated Framework’ 를 개발했습니다."
            ]
        }
    ],
    
    "A": [
        {
            "term": "Access Threats",
            "en": "접근 위협",
            "desc": [
                "허가받지 않은 접근 또는 부적절한 처리가 발생할 위험을 말합니다.",
                "외부 요인에 의한 위험은 일반적으로 보안 위험(Security Risks) 또는 접근성 위험(Accessibility Risks) 이라고 부릅니다.",
                "‘허가되지 않은 행위(Unauthorized Acts)’란 경영진으로부터 정식 권한을 부여받지 않은 사람이나 프로그램이 수행하는 행위를 의미합니다."
            ]
        },
        {
            "term": "Access to Assets",
            "en": "자산 접근 권한",
            "desc": [
                "자산에 대한 접근은 반드시 경영진의 승인과 권한 부여에 따라 허용되어야 합니다."
            ]
        },
        {
            "term": "Accounting",
            "en": "회계",
            "desc": [
                "회계란 기업의 경영활동을 측정하고, 그 정보를 보고서 형태로 처리하여 의사결정자에게 전달하는 정보시스템을 말합니다."
            ]
        },
        {
            "term": "Accounting Cycle",
            "en": "회계 사이클 / 회계순환과정",
            "desc": [
                "기업이 일정 기간 동안의 재무제표를 작성하기 위해 수행하는 일련의 회계 절차를 의미합니다."
            ]
        },
        {
            "term": "Accounting Information System",
            "en": "AIS, 회계정보시스템",
            "desc": [
                "의사결정에 유용한 정보를 제공하기 위해 회계 데이터를 수집·기록·저장·처리하는 시스템입니다."
            ]
        },
        {
            "term": "Activity",
            "en": "활동",
            "desc": [
                "COSO에서는 ‘활동(Activity)’이라는 용어를 급여(payroll) 등과 같은 **운영 단위(Operational Unit)**로 정의합니다.",
                "여기에는 시스템 통제(System Controls) 와 거래처리 통제(Transaction Processing Controls) 가 모두 포함됩니다."
            ]
        },
        {
            "term": "Administrative Control",
            "en": "관리통제",
            "desc": [
                "관리통제는 조직의 계획(Plan), 절차(Procedures), 기록(Records) 등을 포함하며,",
                "이는 경영진의 승인(Authorization) 과 관련된 의사결정 과정을 다룹니다.",
                "이러한 승인 과정은 조직 목표 달성에 대한 경영진의 책임과 직접적으로 연관되며,",
                "회계통제를 수립하는 출발점이 됩니다."
            ]
        },
        {
            "term": "Application Controls",
            "en": "응용통제",
            "desc": [
                "응용 프로그램 내에 설정된 프로그램 절차(Programmed Procedures) 및 관련 수작업 절차로,",
                "정보 처리의 완전성(Completeness) 과 정확성(Accuracy) 을 보장하기 위해 설계된 통제입니다."
            ]
        },
        {
            "term": "Assessment Program",
            "en": "평가 프로그램",
            "desc": [
                "평가 프로그램이란 **시스템이나 활동을 평가하기 위한 절차(Procedure)**를 말합니다.",
                "이는 평가자가 평가를 수행할 때 따라야 하는 **일련의 단계(Steps)**로 구성됩니다.",
                "평가 프로그램은 목표(Objectives) 와 그 목표 달성을 방해할 수 있는 위험(Risks) 을 중심으로 설계되며,",
                "평가 과정에서는 증거(Evidence) 를 수집·기록·검토하는 절차가 반드시 포함됩니다."
            ]
        },
        {
            "term": "Asset Accountability",
            "en": "자산 책임성",
            "desc": [
                "기록된 자산 내역과 실제 자산을 합리적인 주기로 비교하고, 불일치가 발견될 경우 이에 대해 적절한 조치를 취하는 과정을 의미합니다."
            ]
        },
        {
            "term": "Asset Safeguarding",
            "en": "자산 보호",
            "desc": [
                "조직이 보유하거나 취득한 자산이 도난, 남용 등으로부터 충분히 보호될 수 있도록 마련한 절차 및 관리활동을 말합니다."
            ]
        },
        {
            "term": "Audit Committee",
            "en": "감사위원회",
            "desc": [
                "감사위원회는 이사회 내에서 독립적인 위치를 갖는 조직으로, 최고경영진의 재무보고 책임 수행 여부를 검토하고 시정조치를 요구할 권한을 가집니다.",
                "또한 강력한 내부감사 기능과 함께, 경영진이 내부통제를 무력화하거나 재무성과를 왜곡하려는 행위를 식별하고 대응할 수 있는 조직 내 핵심 기구입니다."
            ]
        },
        {
            "term": "Auditability Threats",
            "en": "감사가능성 위협",
            "desc": [
                "거래 처리 과정을 재구성하거나 검증하는 데 필요한 정보를 확보하지 못하게 되는 위험을 의미합니다.",
                "이러한 위협은 감사추적(Audit Trail), 백업(Backup) 및 기록 보존(Historical Records) 목적의 데이터 저장과 관련됩니다.",
                "데이터가 적절히 보관되지 않으면, 경영진·감사인·규제기관이 거래처리를 입증할 증거를 확보하지 못하는 위험이 발생할 수 있습니다."
            ]
        },
        {
            "term": "Authorization",
            "en": "승인",
            "desc": [
                "거래는 경영진의 일반적 또는 개별적 승인을 받아 수행되어야 합니다.",
                "- 일반 승인(General Authorization): 경영진이 정책을 수립하면서 하위 구성원에게 일정 한도 내 거래를 허용하는 경우",
                "- 개별 승인(Specific Authorization): 비정기적이거나 중요도가 높은 거래(예: 대규모 투자지출 등)에 대해 경영진이 사안별로 직접 검토·승인하는 경우",
                "이처럼 승인은 조직의 정책 준수와 통제의 핵심 수단으로 작용합니다."
            ]
        },
        {
            "term": "Automated Controls",
            "en": "자동 통제",
            "desc": [
                "기술을 통해 대부분 또는 전부 수행되는 통제활동을 말합니다.",
                "예를 들어, 컴퓨터 소프트웨어에 프로그래밍된 자동화된 통제 기능 등이 이에 해당합니다.",
                "이는 **수작업 통제(Manual Controls)**와 대비되는 개념으로, 시스템에 내장된 절차를 통해 정확성과 효율성을 향상시키는 통제 방식입니다."
            ]
        }
    ],
    "B": [
        {
            "term": "Benchmarking",
            "en": "벤치마킹",
            "desc": [
                "다른 조직의 우수사례(Best Practices) 와 비교하여 자사 성과를 평가·분석하는 활동을 의미합니다.",
                "이를 통해 효율성, 품질, 경쟁력 향상을 위한 개선 방향을 도출합니다."
            ]
        },
        {
            "term": "Board of Directors",
            "en": "이사회",
            "desc": [
                "주주에 의해 선출되며, 기업의 전략적 목표를 수립하고 조직의 거버넌스(지배구조)를 감독하는 책임을 가진 기관입니다.",
                "기업의 형태에 따라 이사회(Board of Directors), 감독위원회(Supervisory Board),",
                "비영리기관의 경우 이사회(Board of Trustees), 정부기관의 경우 위원회(Board of Governors or Commissioners),",
                "파트너십의 경우 공동경영자(General Partners), 중소기업의 경우 사업주(Owner)가 그 역할을 수행할 수 있습니다.",
                "경영진은 이사회(또는 신탁이사회)에 대해 책임을 지며, 이사회는 조직의 건전한 경영, 윤리적 가치, 투명성 확보를 위한 감독과 지침 역할을 수행합니다.",
                "또한 경영진을 선임하고 그들의 업무를 모니터링함으로써 조직의 청렴성과 윤리 기준을 설정하고 관리하는 핵심 역할을 합니다."
            ]
        },
        {
            "term": "Business System/Application Risk",
            "en": "업무 시스템/응용 프로그램 위험",
            "desc": [
                "조직의 운영과 관련된 활동에서 발생할 수 있는 위험을 의미합니다.",
                "이러한 운영은 일반적으로 비즈니스 사이클(Business Cycle) 형태로 표현되며,",
                "예를 들어 수익 및 비용 사이클(Revenue and Expense Cycle) 과 그 안의 **세부 응용 프로세스(예: 청구, 구매 등)**를 포함합니다.",
                "즉, 비즈니스 시스템이나 애플리케이션의 운영상 결함 또는 오류로 인해 조직의 업무 수행과 통제에 영향을 미칠 수 있는 위험을 의미합니다."
            ]
        }
    ],
    "C": [
        {
            "term": "Cascading of Errors",
            "en": "오류의 연쇄 발생",
            "desc": [
                "전산화된 업무 응용 시스템에서 발생하는 특유의 문제로, 하나의 오류가 연쇄적으로 다른 오류를 유발하는 현상을 의미합니다.",
                "이러한 오류는 사전에 예방하거나 탐지하기가 어렵다는 특징이 있습니다."
            ]
        },
        {
            "term": "Category",
            "en": "카테고리 / 통제목표 범주",
            "desc": [
                "내부통제의 목적(Objectives)을 세 가지 범주로 구분한 것입니다.",
                "이 범주는 운영(Operations), 보고(Reporting), 준법(Compliance) 과 관련됩니다."
            ]
        },
        {
            "term": "Centralized Company",
            "en": "집중형 조직",
            "desc": [
                "주요한 계획, 지휘, 통제 결정이 최고경영진에 의해 이루어지는 기업을 말합니다."
            ]
        },
        {
            "term": "Certified Internal Control Specialist",
            "en": "CICS 국제공인 내부통제사",
            "desc": [
                "내부통제 분야의 선도 전문가들이 개발한 자격 프로그램으로, 내부통제 역량이 일정 수준 이상임을 공식적으로 인정받은 전문가에게 부여됩니다.",
                "CICS 프로그램은 독립적인 인증위원회(Certification Board)가 주관하고, 국제내부통제협회(ICI, Internal Control Institute) 가 관리 및 운영합니다."
            ]
        },
        {
            "term": "Certified Internal Control Professional",
            "en": "CICP 국제공인 전문내부통제사",
            "desc": [
                "내부통제 분야의 고급 전문가를 인증하기 위한 프로그램으로, 내부통제 역량과 실무 경험이 일정 수준 이상임을 검증합니다.",
                "CICP 또한 독립된 인증위원회가 주관하며, 국제내부통제협회(ICI)가 운영합니다."
            ]
        },
        {
            "term": "Certified Public Accountants",
            "en": "CPAs, 공인회계사",
            "desc": [
                "공중(General Public)을 대상으로 서비스를 제공하는 자격을 갖춘 전문 회계인(Licensed Professional Accountants) 을 말합니다."
            ]
        },
        {
            "term": "Chief Executive Officer",
            "en": "CEO, 최고경영자",
            "desc": [
                "기업의 단기 및 장기 계획을 실행할 최고 책임자로, 조직 전체의 내부통제 시스템에 대한 최종 책임을 집니다.",
                "CEO는 Sarbanes–Oxley Act(사베인즈-옥슬리법) 에 따라 내부통제 시스템의 적정성을 공식적으로 증명해야 합니다."
            ]
        },
        {
            "term": "Chief Financial Officer",
            "en": "CFO, 최고재무책임자",
            "desc": [
                "조직의 내부회계통제(Internal Accounting Controls) 에 대한 주요 책임을 가지며,",
                "재무기록, 주주보고서, 성과보고서 등 재무 관련 시스템의 관리 및 통제를 담당합니다."
            ]
        },
        {
            "term": "Chief Operations Officer",
            "en": "COO, 최고운영책임자",
            "desc": [
                "조직의 품질관리(Quality Control) 및 통계적 공정관리(Statistical Process Control) 를 총괄하며,",
                "업무 프로세스 전반과 생산품의 품질에 대한 통제 책임을 집니다."
            ]
        },
        {
            "term": "Cloud Computing",
            "en": "클라우드 컴퓨팅",
            "desc": [
                "소프트웨어와 데이터를 기업 내부가 아닌 제3자의 서버(Third-Party Server) 에 저장하고,",
                "직원이 인터넷을 통해 접근하여 사용하는 컴퓨팅 방식입니다."
            ]
        },
        {
            "term": "Code of Conduct Policy",
            "en": "행동강령 정책",
            "desc": [
                "조직의 직원 행동 기준과 윤리 원칙(Code of Ethics) 을 명문화한 정책으로,",
                "직원이 업무를 수행함에 있어 따라야 할 기본 가치와 행동 지침을 규정합니다."
            ]
        },
        {
            "term": "Code of Ethics",
            "en": "윤리강령",
            "desc": [
                "모든 인증전문가가 준수해야 할 윤리적 행동 기준을 명시한 문서입니다.",
                "해당 강령을 위반할 경우, 인증위원회(Certification Board) 에 의해 자격이 취소될 수 있습니다."
            ]
        },
        {
            "term": "Collusion",
            "en": "담합 / 공모",
            "desc": [
                "두 명 이상이 공모하여 내부통제를 회피하거나 무력화하고, 조직을 부정행위로 손해 입히는 행위를 말합니다."
            ]
        },
        {
            "term": "Committee of Sponsoring Organizations",
            "en": "COSO, 후원단체위원회",
            "desc": [
                "기업위험관리(ERM), 내부통제(Internal Control) 및 부정방지(Fraud Deterrence) 에 관한",
                "사고리더십(Thought Leadership)을 제공하는 국제위원회입니다.",
                "COSO는 내부통제의 표준 프레임워크인 ‘COSO Internal Control – Integrated Framework’ 를 개발했습니다."
            ]
        },
        {
            "term": "Common Body of Knowledge",
            "en": "CBOK, 공통지식체계",
            "desc": [
                "내부통제 자격인증의 기준이 되는 핵심 역량(Skills)과 지식영역을 정의한 체계입니다.",
                "CBOK는 각 내부통제 분야의 전문역량과 기술요소를 분류·정의한 지식 집합으로 구성됩니다."
            ]
        },
        {
            "term": "Compensation Committee",
            "en": "보상위원회",
            "desc": [
                "이사회 산하 위원회로, 조직의 보상체계가 장기적 성과를 저해하지 않도록 설계·감독하는 역할을 합니다.",
                "즉, 단기성과에 치우치지 않고 조직의 지속가능한 목표 달성에 부합하는 보상정책을 관리합니다."
            ]
        },
        {
            "term": "Compliance",
            "en": "준법",
            "desc": [
                "조직에 적용되는 법률 및 규정을 준수하는 상태 또는 그 과정을 의미합니다."
            ]
        },
        {
            "term": "Compliance Hotline",
            "en": "준법 헬프라인 / 윤리신고센터",
            "desc": [
                "현장에서 해결되지 않는 준법 관련 문제를 익명으로 제보하거나 상담할 수 있는 신고채널입니다.",
                "대부분의 경우 연중무휴(24시간·365일) 운영되며, 조직의 윤리·법규 위반 사항을 신고하거나",
                "정보 및 조언을 받을 수 있는 수단을 제공합니다."
            ]
        },
        {
            "term": "Component",
            "en": "구성요소",
            "desc": [
                "내부통제의 다섯 가지 핵심 요소 중 하나를 의미합니다.",
                "내부통제의 구성요소에는 통제환경(Control Environment), 위험평가(Risk Assessment), 통제활동(Control Activities),",
                "정보와 의사소통(Information and Communication), 모니터링활동(Monitoring Activities) 이 포함됩니다."
            ]
        },
        {
            "term": "Computer Processing Controls",
            "en": "컴퓨터 처리 통제",
            "desc": [
                "컴퓨터 처리 과정에서 데이터의 정확성과 완전성을 보장하기 위해 사용되는 통제활동입니다.",
                "이 통제는 데이터가 시스템에 입력된 이후, 응용프로그램이 데이터를 처리하는 동안 적용되며, 프로그램 간 인터페이스, 파일 간 연계, 오류 처리 절차 등을 포함합니다.",
                "또한, 응용프로그램마다 고유하게 설계되어 있어 다른 시스템 간에는 그대로 이전(Transfer)되지 않을 수도 있습니다.",
                "컴퓨터 처리 통제를 개발·유지하는 과정에서는 내부통제의 기본 원칙(예: 직무분리) 이 전산처리 기능에도 지속적으로 반영되도록 해야 합니다."
            ]
        },
        {
            "term": "Conflicts of Interest",
            "en": "이해상충",
            "desc": [
                "개인이 자신의 개인적·사회적·재정적·정치적 이익을 조직의 이익보다 우선시할 때 발생하는 상황을 말합니다.",
                "이해상충이 실제로 존재하지 않더라도 그렇게 보이는 것만으로도 개인과 조직의 명성이 훼손될 수 있습니다.",
                "잠재적인 이해상충은 즉시 상급 관리자에게 보고해야 하며,",
                "정기적으로 조직의 행동강령 또는 윤리기준 준수 확인 절차에서도 이를 명확히 공개해야 합니다.",
                "대부분의 이해상충 문제는 단순한 조정과 상호 합의를 통해 해결이 가능합니다."
            ]
        },
        {
            "term": "Continuing Professional Education",
            "en": "CICS",
            "desc": [
                "CICS 자격 보유자는 자격 유지를 위해 3년마다 총 120시간의 지속적 전문교육(CPE, Continuing Professional Education) 을 이수하거나 재인증 시험을 통과해야 합니다.",
                "CPE는 전문 학회 참석, 교육·훈련 과정 수강, 교육 개발 및 강의, 전문지식 공유, 논문 발표,",
                "전문위원회 또는 특수 관심 그룹 활동 참여 등을 통해 이수할 수 있습니다."
            ]
        },
        {
            "term": "Control",
            "en": "통제",
            "desc": [
                "목표 달성 과정에서 발생할 수 있는 위험을 최소화하거나 제거하기 위한 모든 방법·기법·절차를 의미합니다.",
                "명사로서(Control): 내부통제의 일부로 존재하는 정책 또는 절차를 의미하며, 이는 5대 구성요소 각각에 내재되어 있습니다.",
                "동사로서(to control): 정책이나 절차를 수립·실행하여 내부통제 원칙을 구현하는 행위를 뜻합니다."
            ]
        },
        {
            "term": "Control Account",
            "en": "통제계정",
            "desc": [
                "보조원장에서 관리되는 관련 계정들의 잔액 합계와 일치하는 잔액을 가진 주계정을 말합니다."
            ]
        },
        {
            "term": "Control Activities",
            "en": "통제활동",
            "desc": [
                "경영진의 지시사항이 제대로 수행되도록 보장하는 정책과 절차입니다.",
                "조직 전체의 모든 수준과 기능에서 이루어지며,",
                "승인(Approvals), 권한부여(Authorizations), 검증(Verifications), 대사(Reconciliations),",
                "운영성과 검토(Review of Performance), 자산보호(Security of Assets), 직무분리(Segregation of Duties) 등 다양한 활동을 포함합니다."
            ]
        },
        {
            "term": "Control Environment",
            "en": "통제환경",
            "desc": [
                "조직의 핵심은 사람이며, 그들의 정직성, 윤리적 가치, 역량과 이를 둘러싼 조직문화와 경영환경이 내부통제의 근간을 이룹니다.",
                "통제환경은 조직의 통제 의식(Control Consciousness) 을 형성하고,",
                "모든 내부통제 구성요소의 기반으로 규율(Discipline) 과 구조(Structure) 를 제공합니다.",
                "주요 요소에는 조직 구성원의 정직성과 윤리 가치, 경영진의 철학과 운영 방식, 권한과 책임의 부여 방식, 인사 관리, 이사회의 감독 및 방향 제시 등이 포함됩니다."
            ]
        },
        {
            "term": "Control Hierarchy",
            "en": "통제계층구조",
            "desc": [
                "내부통제를 계층적으로 분류한 체계를 의미합니다.",
                "가장 상위에는 환경통제(Environmental Controls) 가 있으며,",
                "그 아래에 시스템통제(System Controls), 그 아래 단계에는 거래처리통제(Transaction Processing Controls) 가 위치합니다.",
                "상위 통제는 하위 통제의 운영과 효과성에 영향을 미칩니다."
            ]
        },
        {
            "term": "Control Objectives",
            "en": "통제목표",
            "desc": [
                "위험에 대한 긍정적 진술(Positive Statement) 로, 내부통제의 기대 결과를 정의합니다.",
                "예를 들어, “거래가 누락될 위험이 있다”는 위험에 대응하는 통제목표는",
                "“모든 거래가 정확히 처리된다”가 될 수 있습니다.",
                "통제목표는 회계문헌, 미국 해외부패방지법(FCPA), 사베인즈-옥슬리법(SOX) 등에 언급된 일반적인 시스템 통제목표를 포함합니다."
            ]
        },
        {
            "term": "Control Point",
            "en": "통제지점",
            "desc": [
                "응용시스템 내에서 손실 가능성이 가장 크고, 통제가 설치되어야 하는 핵심 지점을 의미합니다."
            ]
        },
        {
            "term": "Control Threats",
            "en": "통제위협",
            "desc": [
                "데이터의 정확성과 완전성, 그리고 응용시스템의 운영 성능을 위협하는 요소를 말합니다.",
                "이는 시스템 내 데이터의 무결성, 통제의 효과성, 경제성, 효율성에 영향을 미칠 수 있는 위험을 포함합니다."
            ]
        },
        {
            "term": "Corporate Compliance Committee",
            "en": "기업준법위원회",
            "desc": [
                "기업의 준법감시기능(Compliance Function) 을 강화하기 위해 설치된 위원회로,",
                "각 사업부문 대표들이 참여하여 준법 전략과 시스템을 감독합니다.",
                "위원회는 준법책임자, 이사회, 최고경영진에게 주요 준법 이슈, 위험, 동향을 보고하고 관리합니다."
            ]
        },
        {
            "term": "Corporate Compliance Officer",
            "en": "기업준법책임자",
            "desc": [
                "기업의 준법 시스템을 총괄 감독하며, 내부감사·모니터링·자체평가 프로그램을 통해",
                "조직이 법적·규제적 의무를 준수하도록 보장합니다.",
                "준법책임자는 기업 전반에서 표준의 일관된 적용과 해석을 유지하고,",
                "일반적으로 이사회 의장, CEO, 감사위원회에 직접 보고합니다."
            ]
        },
        {
            "term": "Corporate Governance",
            "en": "기업지배구조",
            "desc": [
                "기업지배구조는 보편적 정의가 존재하지 않으나, 일반적으로 조직의 경영활동을 통제·감독·지도하는 구조와 절차를 의미합니다.",
                "사전적 의미로 “통치(govern)”는 권한을 행사하고, 행동을 지도하며, 통제하는 것을 뜻합니다.",
                "기업지배구조는 주로 이사회(Board of Directors) 와 경영진(Senior Management) 이",
                "기업을 건전하게 운영하기 위해 수행하는 관리활동의 집합으로 이해됩니다."
            ]
        },
        {
            "term": "Corporate Values",
            "en": "기업가치",
            "desc": [
                "기업이 일상적인 운영에서 중요하게 여기는 핵심 가치(Core Values) 를 의미합니다.",
                "예를 들어 “모든 구성원을 존중한다”는 가치가 있다면, 직원의 부적절한 행동을 공개적으로 질책하는 것은",
                "그 가치에 위배되는 행위가 됩니다."
            ]
        },
        {
            "term": "Corporation",
            "en": "법인",
            "desc": [
                "국가 또는 주(州) 법률에 의해 설립된 독립적인 법적 실체(Legal Entity) 로,",
                "소유주와 분리된 권리와 의무를 가집니다."
            ]
        },
        {
            "term": "Corrective Control",
            "en": "시정통제",
            "desc": [
                "바람직하지 않은 사건이 발생했을 때, 이를 수정하거나 원인을 제거하기 위한 정보나 증거를 제공하는 통제활동입니다.",
                "시정통제는 일반적으로 탐지통제(Detection Control) 의 결과를 기반으로 적용됩니다."
            ]
        },
        {
            "term": "COSO",
            "en": "코소",
            "desc": [
                "Committee of Sponsoring Organizations of the Treadway Commission 의 약칭으로,",
                "내부통제 평가와 기업위험관리(ERM)에 관한 지침을 제공하기 위해 설립된 위원회입니다.",
                "COSO는 다음 다섯 기관으로 구성됩니다:",
                "- 미국재무임원협회(FEI)",
                "- 미국공인회계사협회(AICPA)",
                "- 미국회계학회(AAA)",
                "- 내부감사인협회(IIA)",
                "- 경영회계사협회(IMA)",
                "COSO는 기업위험관리(ERM), 내부통제, 부정방지(Fraud Deterrence) 분야에서",
                "국제적 프레임워크와 가이드라인을 개발하는 리더십 기관입니다."
            ]
        },
        {
            "term": "COSO’s Enterprise Risk Management Framework",
            "en": "COSO 기업위험관리 프레임워크",
            "desc": [
                "조직의 비즈니스 목표를 달성하기 위한 구조적 체계를 제공합니다.",
                "경영진은 조직의 사명(Mission)과 제약조건(Constraints)에 부합하면서도",
                "목표 달성 가능성을 높이는 전략적 비즈니스 목표를 수립할 책임이 있습니다.",
                "COSO의 기업위험관리 프레임워크(ERM Framework) 는 이러한 목표를 설정하고 달성하기 위한",
                "체계적인 위험관리 및 통제 접근방식을 제시합니다."
            ]
        },
        {
            "term": "COSO’s Internal Control Framework",
            "en": "COSO 내부통제 프레임워크",
            "desc": [
                "COSO 내부통제 프레임워크는 미국 증권거래위원회(SEC) 에 의해 내부통제를 평가하기 위한 유효한 기준으로 공식 인정받고 있습니다.",
                "이 프레임워크는 경영진이 조직을 운영하는 방식에서 도출된 상호 연관된 다섯 가지 구성요소로 이루어져 있으며, 경영 프로세스 전반에 통합되어 있습니다.",
                "다섯 가지 구성요소는 통제환경(Control Environment), 위험평가(Risk Assessment), 통제활동(Control Activities), 정보와 의사소통(Information and Communication), 모니터링(Monitoring Activities) 입니다.",
                "COSO 내부통제 프레임워크는 현재 전 세계적으로 가장 널리 인정받는 내부통제 모델로 자리 잡았습니다."
            ]
        }
    ],
    "D": [
        {
            "term": "Data Communications Controls",
            "en": "데이터 통신 통제",
            "desc": [
                "데이터 통신 통제는 메시지 입력 장치에서 수신 장치로 데이터를 전송하는 과정에서 데이터의 무결성을 보장하기 위한 통제입니다.",
                "이러한 통제는 대부분의 데이터 통신 장비가 송신 또는 수신 기관이 아닌 외부 기관에 의해 소유·관리되기 때문에 중요합니다.",
                "또한 최근 많은 조직이 데이터 통신 서비스를 응용 시스템의 필수 구성요소로 활용함에 따라, ",
                "전체 시스템의 데이터 정확성과 완전성을 확보하기 위해 더욱 중요해지고 있습니다."
            ]
        },
        {
            "term": "Data Storage and Retrieval Controls",
            "en": "데이터 저장 및 검색 통제",
            "desc": [
                "데이터 저장 및 검색 통제는 파일 처리 및 파일 오류 처리 과정에서 적용되는 통제활동을 포함합니다.",
                "이는 응용 시스템의 전산처리와 직접 관련되지 않은 파일 관리 절차를 통제하며, 데이터 저장 및 검색 중 데이터 파일과 프로그램의 무결성을 유지하기 위한 시설 관리 및 인력 절차가 중요합니다.",
                "이 영역은 사람의 개입이 많고 수작업 데이터 처리 비중이 높기 때문에, 통제 절차의 일관성과 정확성이 특히 강조됩니다."
            ]
        },
        {
            "term": "Decentralized Company",
            "en": "분권형 조직",
            "desc": [
                "사업 부문별로 나뉘어 있으며, 각 부문 관리자가 해당 부문의 계획, 운영, 통제와 관련된 의사결정 권한을 가지는 기업 구조를 말합니다."
            ]
        },
        {
            "term": "Detective Control",
            "en": "탐지 통제",
            "desc": [
                "초기 처리 이후에 발생한 의도치 않은 사건이나 결과를 발견하기 위해 설계된 통제입니다.",
                "설계 목적에 따라 통제는 일반적으로 예방통제(Preventive Control) 와 탐지통제(Detective Control) 로 구분되며,",
                "탐지통제는 문제가 발생한 후 목표 달성 전에 오류나 이상을 식별하여 수정 조치를 취할 수 있게 합니다."
            ]
        },
        {
            "term": "Disclosure Controls and Procedures",
            "en": "공시 통제 및 절차",
            "desc": [
                "미국 증권거래위원회(SEC) 의 인증 규칙에 정의된 개념으로,",
                "발행자가 거래법(Exchange Act) 에 따라 보고해야 하는 정보가 기한 내에 기록·처리·요약·보고되도록 보장하는 통제 및 절차를 의미합니다.",
                "이는 또한, 공시 대상 정보가 적시에 경영진에게 전달되어 필요한 공시 결정을 내릴 수 있도록 보장하는 절차를 포함합니다."
            ]
        },
        {
            "term": "Disclosure Principle",
            "en": "공시 원칙",
            "desc": [
                "기업의 재무제표는 외부 이해관계자가 기업에 대해 충분히 이해하고 합리적인 의사결정을 내릴 수 있도록 충분한 정보를 제공해야 한다는 원칙입니다."
            ]
        }
    ],
    "E": [
        {
            "term": "Effected",
            "en": "실행된 / 적용된",
            "desc": [
                "내부통제 시스템과 관련하여 사용되는 용어로, ‘수립되고 유지되는(devised and maintained)’ 상태를 의미합니다."
            ]
        },
        {
            "term": "Effective Internal Control",
            "en": "효과적인 내부통제",
            "desc": [
                "효과적인 내부통제 시스템은 조직이 목표를 달성할 수 있도록 합리적 보증(reasonable assurance) 을 제공합니다.",
                "이를 위해 내부통제의 다섯 가지 구성요소와 관련 원칙이 존재하고 제대로 기능해야 하며,",
                "이 다섯 구성요소가 상호 연계되어 일관되게 작동해야 합니다."
            ]
        },
        {
            "term": "Electronic Data Interchange",
            "en": "EDI, 전자자료교환",
            "desc": [
                "종이 문서를 완전히 배제하고, 고객과 공급자의 컴퓨터 간에 직접 통신을 통해 일상적인 거래를 자동화하는 시스템입니다.",
                "EDI는 문서 흐름을 단순화하고 거래 속도와 정확성을 높입니다."
            ]
        },
        {
            "term": "Electronic Funds Transfer",
            "en": "EFT, 전자자금이체",
            "desc": [
                "종이 문서가 아닌 전자통신(Electronic Communication) 을 통해 자금을 이체하는 시스템입니다.",
                "예: 인터넷뱅킹, 자동이체, 전자결제 등"
            ]
        },
        {
            "term": "Enterprise Risk Management",
            "en": "ERM, 전사적 위험관리",
            "desc": [
                "전사적 위험관리(ERM)는 경영진의 기업 운영 방식에서 파생된 8개의 상호 연관된 구성요소로 이루어져 있습니다.",
                "이 구성요소는 경영 프로세스 전반에 통합되어 있으며 다음과 같습니다:",
                "1. 내부환경(Internal Environment)",
                "2. 목표설정(Objective Setting)",
                "3. 사건식별(Event Identification)",
                "4. 위험평가(Risk Assessment)",
                "5. 위험대응(Risk Response)",
                "6. 통제활동(Control Activities)",
                "7. 정보 및 의사소통(Information and Communication)",
                "8. 모니터링(Monitoring)",
                "ERM은 조직의 사명과 목표 달성을 위한 통합적 위험관리 체계를 제공합니다."
            ]
        },
        {
            "term": "Entity",
            "en": "조직체 / 법인체",
            "desc": [
                "특정 목적을 위해 설립된 법적 실체 또는 관리 운영 모델을 의미합니다.",
                "예를 들어, 영리기업, 비영리단체, 정부기관, 교육기관 등이 이에 해당합니다.",
                "관리운영 모델은 제품·서비스 라인, 사업부(Division), 운영단위(Unit) 등의 형태로 구성될 수 있으며,",
                "지리적 구분에 따라 더 세분화되거나 통합될 수도 있습니다."
            ]
        },
        {
            "term": "Entity-level",
            "en": "조직 수준 / 개별 단위 상위 수준",
            "desc": [
                "조직 전체 수준에서 이루어지는 활동으로, 자회사·사업부·운영단위·기능 등 개별 단위와 구분되는 상위 수준의 통제나 활동을 의미합니다."
            ]
        },
        {
            "term": "Entity-wide",
            "en": "전사적 / 조직 전체적",
            "desc": [
                "조직 전체에 적용되는 활동을 의미하며, 주로 전사적 통제(Entity-wide Controls) 와 관련됩니다."
            ]
        },
        {
            "term": "Environmental Controls",
            "en": "환경통제",
            "desc": [
                "환경통제는 경영진의 책임 하에 설정되며, 조직 운영 방식과 가치관을 반영합니다.",
                "즉, 경영진이 설정한 가치와 업무 수행 방식이 조직의 환경(Environment)을 형성하고,",
                "이 환경이 조직의 업무 수행 방식과 운영 전반에 중대한 영향을 미칩니다.",
                "따라서 환경통제는 조직의 행동기준과 성과문화에 직접적인 영향을 미치는 핵심 통제입니다."
            ]
        },
        {
            "term": "Environmental Risk",
            "en": "환경적 위험",
            "desc": [
                "조직의 내부 및 외부 환경 요인과 관련된 위험을 의미합니다.",
                "예: 시장변화, 규제환경, 사회적 책임, 내부 조직문화 등"
            ]
        },
        {
            "term": "Ethical Values",
            "en": "윤리적 가치",
            "desc": [
                "의사결정자가 적절한 행동 방향을 판단할 수 있도록 하는 도덕적 기준을 의미합니다.",
                "이러한 가치는 단순히 합법적인 행위에 그치지 않고, ‘옳은 것(What is Right)’ 에 근거해야 합니다."
            ]
        },
        {
            "term": "Expenditures Cycle",
            "en": "지출사이클",
            "desc": [
                "지출사이클은 구매(Purchasing), 급여(Payroll), 지급(Disbursement) 의 세 가지 기능으로 구분됩니다.",
                "즉, 조직의 비용이 발생하고 지급되는 전 과정을 포괄하는 회계주기입니다."
            ]
        },
        {
            "term": "Exposure",
            "en": "노출 / 위험노출",
            "desc": [
                "‘노출’이란 조직이 손실에 노출될 수 있는 사건 또는 상황을 의미합니다.",
                "이는 실제 손실을 의미하는 것은 아니며, 손실이 발생할 가능성(Potential for Loss) 이 존재함을 나타냅니다.",
                "예를 들어, “무단거래(Unauthorized Transactions)”라는 노출은 손실이 발생했다는 의미가 아니라,",
                "해당 거래가 시스템 내에서 탐지·삭제되어 손실이 방지될 수도 있음을 의미합니다.",
                "즉, 노출은 위협(Threat)으로부터 발생할 수 있는 잠재적 손실 규모(금전적 또는 비금전적 가치) 를 나타냅니다."
            ]
        },
        {
            "term": "External Auditing",
            "en": "외부감사",
            "desc": [
                "독립된 공인회계사(Independent Certified Public Accountants)가 수행하는 감사로,",
                "조직의 재무보고 목적 달성에 기여합니다.",
                "외부감사인은 객관적이고 독립적인 시각을 제공하여, 경영진과 이사회가 재무보고 책임을 충실히 이행하도록 지원합니다."
            ]
        },
        {
            "term": "External Auditor",
            "en": "외부감사인",
            "desc": [
                "기업과 독립된 외부 회계전문가로서, 내부통제를 평가하고 재무제표가 일반적으로 인정된 회계원칙(GAAP) 에 따라 공정하게 작성되었는지를 검증합니다."
            ]
        },
        {
            "term": "External Financial Reporting Cycle",
            "en": "외부재무보고 사이클",
            "desc": [
                "외부재무보고 사이클은 다음과 같은 절차를 포함하는 과정입니다:",
                "- 분개 및 거래의 일반원장 반영",
                "- 적용할 회계기준(GAAP)의 결정",
                "- 재무제표 및 외부보고서 작성을 위한 정보 수집 및 통합",
                "- 재무제표 및 관련 공시사항의 작성·검토",
                "이 사이클은 사베인즈-옥슬리법(Sarbanes-Oxley Act) 제정 이후, 기업의 투명성과 책임성 강화를 위해 더욱 중요해졌습니다."
            ]
        }
    ],
    "F": [
        {
            "term": "Finance Committee",
            "en": "재무위원회",
            "desc": [
                "이사회 산하의 위원회로, 대규모 자금 집행 및 자본지출 예산의 운영계획과의 일치 여부를 검토·통제하는 역할을 합니다.",
                "즉, 조직의 재정적 의사결정이 경영전략과 일관되도록 관리하는 기능을 수행합니다."
            ]
        },
        {
            "term": "Financial Officers",
            "en": "재무담당 임원",
            "desc": [
                "재무 및 관리회계 담당 임원과 그 조직은 기업의 모든 운영 단위를 수평적·수직적으로 관통하는 핵심 기능을 수행합니다.",
                "이들은 조직 전반의 예산 및 계획 수립, 성과 분석, 운영·재무·준법 측면에서의 관리활동을 수행합니다.",
                "따라서 최고재무책임자(CFO), 최고회계책임자(CAO), Controller(관리회계책임자) 등은",
                "경영진이 통제를 실행하는 데 있어 핵심적인 역할을 담당합니다."
            ]
        },
        {
            "term": "Financial Statements",
            "en": "재무제표",
            "desc": [
                "일반적으로 다음의 항목으로 구성됩니다:",
                "1. 재무상태표(Statement of Financial Position)",
                "2. 손익계산서(Statement of Income)",
                "3. 자본변동표(Statement of Changes in Equity)",
                "4. 현금흐름표(Statement of Cash Flows)",
                "5. 주석(Notes to the Financial Statements)",
                "이들은 조직의 재무성과와 재무상태를 이해하기 위한 기본 보고서입니다."
            ]
        },
        {
            "term": "Financing Cycle",
            "en": "자금조달 사이클",
            "desc": [
                "기업의 자본금 발행 및 상환, 배당금 지급, 리스 등 자금조달 수단의 검토 및 선택,",
                "채무 관리(계약이행 및 약정조건 준수 모니터링), 투자관리 및 유가증권의 보관관리 등",
                "자금의 조달과 운용에 관련된 일련의 활동을 포함합니다."
            ]
        },
        {
            "term": "Foreign Corrupt Practices Act",
            "en": "FCPA, 해외부패방지법",
            "desc": [
                "1977년에 제정된 미국 연방법으로, 부적절한 내부회계통제(Inadequate Internal Accounting Control) 를",
                "연방 범죄(Federal Offense) 로 규정하였습니다.",
                "이 법은 기업이 부패행위 및 부정회계를 방지하기 위해 ‘적정 수준의 내부회계통제(Adequate Internal Controls)’ 를 유지하도록 요구합니다."
            ]
        },
        {
            "term": "Freedom of Information Act",
            "en": "정보자유법",
            "desc": [
                "미국 연방법으로, 연방기관(Federal Agencies) 이 개인 관련 정보를 포함한 특정 정보를",
                "연방관보(Federal Register) 에 공개하도록 규정합니다.",
                "또한, 기관이 보유한 정보의 출처(Source) 와 획득방법(Method) 도 공개되어야 하며,",
                "국민이 정부가 보유한 정보에 접근할 수 있는 투명성과 접근권(Right to Know) 을 보장합니다."
            ]
        }
    ],
    "G": [
        {
            "term": "Generally Accepted Accounting Principles",
            "en": "GAAP, 일반적으로 인정된 회계원칙",
            "desc": [
                "미국 재무회계기준위원회(FASB, Financial Accounting Standards Board) 가 제정한 회계 지침과 기준으로,",
                "미국 내에서 회계처리와 재무보고를 수행할 때 적용되는 주요 회계 규범집(Main U.S. Accounting Rule Book) 을 의미합니다.",
                "즉, 기업이 재무제표를 작성할 때 따라야 하는 표준화된 회계원칙 체계입니다."
            ]
        }
    ],
    "I": [
        {
            "term": "ICI Risk and Control Model",
            "en": "ICI 위험 및 통제모델",
            "desc": [
                "ICI(국제내부통제협회)의 위험 및 통제모델은 3단계 구조(Three-Level Model) 로 구성되어 있습니다.",
                "세 단계는 환경통제(Environmental Controls), 시스템통제(System Controls), 거래처리통제(Transaction Processing Controls) 입니다.",
                "- 환경통제(Environmental Controls) 는 이사회(Board of Directors) 와 최고경영진(Executive Management) 의 책임으로, 시스템 및 거래처리 통제가 의도한 대로 수행될 수 있는 환경을 조성합니다.",
                "- 시스템통제(System Controls) 는 중간관리자(Middle Management) 의 책임으로, 예를 들어 급여(payroll) 등 특정 기능을 담당하는 부서에서 수행됩니다.",
                "- 거래처리통제(Transaction Processing Controls) 는 직원(Employee) 의 책임으로, 개별 거래 수준에서 통제를 수행합니다."
            ]
        },
        {
            "term": "Information and Communication",
            "en": "정보 및 의사소통",
            "desc": [
                "정보 및 의사소통 시스템은 조직의 사람들로 하여금 업무 수행, 관리, 통제에 필요한 정보를 수집하고 교환할 수 있도록 지원하는 활동을 의미합니다.",
                "이 시스템은 내부통제의 다른 구성요소를 연결하는 핵심 기반요소입니다."
            ]
        },
        {
            "term": "Inherent Limitations",
            "en": "내재적 한계",
            "desc": [
                "모든 내부통제 시스템이 가지는 한계적 특성을 의미합니다.",
                "이에는 내부통제의 전제조건, 조직이 통제할 수 없는 외부 사건, 인간 판단의 한계, 시스템 오류나 통제 실패 가능성,",
                "그리고 경영진의 통제 무력화(Management Override) 및 담합(Collusion) 가능성이 포함됩니다."
            ]
        },
        {
            "term": "Inherent Risk",
            "en": "고유위험 / 내재위험",
            "desc": [
                "경영진이 위험의 발생 가능성(Likelihood) 이나 영향(Impact) 을 완화하기 위한 어떠한 조치도 취하지 않은 상태에서,",
                "조직의 목표 달성에 영향을 미칠 수 있는 본질적 위험 수준을 의미합니다."
            ]
        },
        {
            "term": "Integrity",
            "en": "정직성 / 진실성",
            "desc": [
                "도덕적 원칙에 입각한 올바름, 성실함, 진정성을 의미합니다.",
                "정직성은 ‘옳은 일을 하고자 하는 의지’이며, 조직이 추구하는 가치와 기대 기준을 스스로 지키려는 태도를 포함합니다."
            ]
        },
        {
            "term": "Internal Audit Charter",
            "en": "내부감사 헌장",
            "desc": [
                "이사회(Board of Directors)가 검토·승인한 공식 문서로,",
                "내부감사 기능의 권한(Authority) 과 책임(Responsibility) 을 명시합니다.",
                "내부감사 헌장은 감사부서가 경영진 및/또는 이사회에 독립적으로 보고할 권한을 규정합니다."
            ]
        },
        {
            "term": "Internal Auditing",
            "en": "내부감사",
            "desc": [
                "조직 내부에 설치된 기능으로, 조직의 운영성과, 통제의 적정성 및 준수 여부를 평가할 권한과 책임을 가집니다.",
                "국제내부감사인협회(IIA) 의 「내부감사 전문실무기준(Standards for the Professional Practice of Internal Auditing)」에 따르면,",
                "내부감사는 조직의 가치를 높이고 개선하기 위한 독립적이고 객관적인 보증(Assurance) 및 자문(Consulting) 활동입니다.",
                "내부감사는 체계적이고 규율된 접근법(Systematic and Disciplined Approach) 을 통해",
                "조직의 위험관리, 통제, 거버넌스 프로세스의 효과성을 평가하고 개선합니다."
            ]
        },
        {
            "term": "Internal Auditors",
            "en": "내부감사인",
            "desc": [
                "조직의 직원으로서, 회사의 정책 준수 여부, 법규 준수 상태, 운영의 효율성을 점검합니다.",
                "내부감사인은 내부통제 시스템을 직접 검토하고 개선사항을 제안합니다.",
                "IIA의 기준에 따르면, 내부감사의 범위에는 내부통제 시스템의 적정성 및 효과성 평가,",
                "그리고 각 부문의 성과 품질에 대한 평가가 포함됩니다."
            ]
        },
        {
            "term": "Internal Control",
            "en": "내부통제",
            "desc": [
                "미국공인회계사협회(AICPA) 의 정의에 따르면,",
                "내부통제는 조직 내에서 채택된 계획(Plan), 절차(Procedures), 방법(Measures) 의 집합으로,",
                "다음의 목적을 달성하기 위해 설계됩니다:",
                "- 자산의 보호(Safeguarding of Assets)",
                "- 회계정보의 정확성과 신뢰성 확보(Accuracy and Reliability of Accounting Data)",
                "- 운영 효율성 증진(Operational Efficiency)",
                "- 경영방침의 준수(Adherence to Managerial Policies)",
                "이 정의는 내부통제가 회계부서나 재무부서의 기능을 넘어, 조직 전체의 통합적 관리체계임을 강조합니다."
            ]
        },
        {
            "term": "Internal Control Deficiency",
            "en": "내부통제 결함",
            "desc": [
                "내부통제 구성요소 또는 관련 원칙 중 하나 이상에 존재하는 부족함이나 결함으로 인해,",
                "조직이 목표를 달성할 가능성을 낮추는 상태를 의미합니다."
            ]
        },
        {
            "term": "Internal Control Report",
            "en": "내부통제 보고서",
            "desc": [
                "경영진이 작성하는 보고서로, 재무보고에 대한 내부통제의 적정성 및 그에 대한 경영진의 책임을 기술합니다."
            ]
        },
        {
            "term": "Internal Control System Design",
            "en": "내부통제 시스템 설계",
            "desc": [
                "1. 의도(Intent): 내부통제의 목적은 조직이 목표를 달성할 수 있도록 합리적 보증을 제공하는 것입니다.",
                "이 의도가 실현될 경우 시스템은 효과적인 것으로 간주됩니다.",
                "2. 계획(Plan): 시스템이 설계된 방식(How it is supposed to work)과 실제 운영 방식(How it actually works)을 구분하여 관리하는 것을 의미합니다."
            ]
        },
        {
            "term": "International Accounting Standards Board",
            "en": "IASB, 국제회계기준위원회",
            "desc": [
                "국제재무보고기준(IFRS) 의 제정과 운영을 감독하는 민간 국제기구로,",
                "글로벌 회계기준의 일관성과 투명성을 확보하기 위한 역할을 수행합니다."
            ]
        },
        {
            "term": "International Financial Reporting Standards",
            "en": "IFRS, 국제재무보고기준",
            "desc": [
                "국제회계기준위원회(IASB) 에 의해 제정된 전 세계적으로 통용되는 회계기준입니다.",
                "IFRS는 국가 간 회계기준의 일관성을 높이고, 투자자와 이해관계자들이 비교 가능한 재무정보를 활용할 수 있도록 설계되었습니다."
            ]
        }
    ],
    "K": [
        {
            "term": "Key Performance Indicator",
            "en": "KPI, 핵심성과지표",
            "desc": [
                "조직이나 부서가 목표를 얼마나 효과적으로 달성하고 있는지를 측정하는 핵심 성과 지표를 의미합니다.",
                "KPI는 경영진이 조직의 성과를 평가하고 전략적 목표 달성 여부를 판단하는 데 활용되는 요약된 성과 측정지표(Summary Performance Measure) 입니다."
            ]
        }
    ],
    "M": [
        {
            "term": "Major Deficiency",
            "en": "중대한 결함",
            "desc": [
                "조직이 목표를 달성할 가능성을 심각하게 저해하는 내부통제의 결함 또는 결함들의 결합을 의미합니다.",
                "이는 내부통제 시스템의 효과성에 중대한 영향을 미치는 수준의 약점을 지칭합니다."
            ]
        },
        {
            "term": "Malcolm Baldrige National Quality Award",
            "en": "말콤 볼드리지 국가품질상",
            "desc": [
                "미국 기업의 경영 효율성과 효과성 향상을 촉진하기 위해 제정된 말콤 볼드리지 경영모델(Malcolm Baldrige Management Model) 을 기반으로 한 국가 품질상입니다.",
                "이 상은 일본의 데밍상(Deming Prize) 에서 영감을 받아 설립되었으며,",
                "두 상의 목적은 다음 두 가지입니다.",
                "1. 자신의 산업 분야에서 선도적인 기업을 인정(Recognition)",
                "2. 우수 사례(Best Practices)를 공유하여 다른 기업의 경영성과를 향상(Improvement)"
            ]
        },
        {
            "term": "Management Intervention",
            "en": "경영진 개입",
            "desc": [
                "경영진이 비정상적이거나 비정기적인 거래 또는 사건에 대해,",
                "기존의 정책이나 절차를 정당한 목적(Legitimate Purpose) 으로 일시적으로 변경하거나 예외를 적용하는 행위를 의미합니다."
            ]
        },
        {
            "term": "Management Override",
            "en": "경영진의 통제무력화",
            "desc": [
                "경영진이 개인적 이익 또는 조직의 재무상태나 준수현황을 왜곡하여 유리하게 보이게 하려는 목적으로",
                "기존의 정책이나 절차를 부당하게(overrule) 무시하거나 변경하는 행위를 말합니다.",
                "이는 내부통제의 신뢰성을 훼손하는 대표적인 위험 요소입니다."
            ]
        },
        {
            "term": "Management Process",
            "en": "경영 프로세스",
            "desc": [
                "경영진이 조직을 운영하기 위해 수행하는 일련의 계획, 실행, 평가, 개선 활동의 흐름을 의미합니다.",
                "내부통제 시스템은 이러한 경영 프로세스의 통합적 구성요소(Integrated Component) 중 하나로 작동합니다."
            ]
        },
        {
            "term": "Management’s Discussion and Analysis of Financial Condition and Results of Operations",
            "en": "MD&A, 경영진의 재무상태 및 영업실적 분석보고",
            "desc": [
                "연차보고서(Annual Report)의 한 섹션으로,",
                "투자자가 기업의 재무상태, 영업성과, 미래 전망을 이해할 수 있도록",
                "경영진이 작성하는 설명 및 분석 보고서입니다."
            ]
        },
        {
            "term": "Manual Controls",
            "en": "수작업 통제",
            "desc": [
                "기술적 자동화가 아닌, 사람이 직접 수행하는 통제활동을 의미합니다.",
                "예: 서면 승인, 수동 대사(Reconciliation), 문서 검토 등",
                "이는 자동통제(Automated Controls) 와 대비되는 개념입니다."
            ]
        },
        {
            "term": "Materiality",
            "en": "중요성",
            "desc": [
                "통제를 결정하는 주요 기준으로, 거래의 금액적 중요성 또는 상대적 가치를 의미합니다.",
                "중요성은 보호 대상 사건의 가치가 조직 전체 가치에서 차지하는 비율에 따라 달라집니다.",
                "즉, 가치가 클수록 강력한 통제가 필요하며,",
                "통제 강도와 보호가치 간에는 높은 상관관계가 존재해야 합니다."
            ]
        },
        {
            "term": "Materiality Concept",
            "en": "중요성 개념",
            "desc": [
                "기업은 재무상태에 중대한 영향을 미치는 항목에 대해서만",
                "엄격한 회계처리를 수행해야 한다는 원칙입니다.",
                "즉, 중요하지 않은 항목은 실무 효율성을 위해 단순화할 수 있습니다."
            ]
        },
        {
            "term": "Monitoring",
            "en": "모니터링",
            "desc": [
                "내부통제 시스템의 품질과 효과성을 지속적으로 평가하는 과정입니다.",
                "이는 다음 두 가지 형태로 수행됩니다.",
                "- 상시 모니터링(Ongoing Monitoring):",
                "일상적인 운영활동 속에서 이루어지는 경영진 및 감독활동,",
                "그리고 직원이 업무 수행 중 수행하는 점검활동 등을 포함합니다.",
                "- 별도평가(Separate Evaluations):",
                "정기적 또는 비정기적으로 수행되는 독립적인 통제점검 및 평가활동으로,",
                "수행 빈도와 범위는 위험 수준과 기존 모니터링의 효과성에 따라 결정됩니다.",
                "모니터링 결과 식별된 내부통제 결함은 상위 보고 체계(Upstream Reporting) 를 통해 전달되어야 하며,",
                "중대한 사안은 반드시 최고경영진 및 이사회(Board of Directors) 에 보고되어야 합니다."
            ]
        }
    ],
    "N": [
        {
            "term": "Network",
            "en": "네트워크",
            "desc": [
                "서로 다른 컴퓨터들이 동일한 정보를 공유할 수 있도록 전자적으로 연결된 시스템을 의미합니다.",
                "즉, 데이터와 자원을 교환하기 위해 구성된 정보통신 연결망입니다."
            ]
        },
        {
            "term": "Nominating Committee",
            "en": "후보추천위원회",
            "desc": [
                "이사회 산하 위원회로, 이사 후보자 및 최고경영진(Top Management) 후보자의 선임 과정을 관리·통제하는 역할을 합니다.",
                "조직의 지배구조(Governance) 투명성과 독립성을 확보하기 위한 핵심적인 내부통제 기능 중 하나입니다."
            ]
        }
    ],
    "O": [
        {
            "term": "Operating Together",
            "en": "통합 작동 / 상호운영",
            "desc": [
                "내부통제의 다섯 가지 구성요소가 상호 연계되어 조직의 목표 달성 실패 위험을 허용 가능한 수준으로 낮추는 상태를 의미합니다.",
                "즉, 각 구성요소가 개별적으로 기능할 뿐만 아니라, 통합적으로 작동하여 전체 내부통제 시스템의 효과성을 확보하는 것을 뜻합니다."
            ]
        },
        {
            "term": "Operational Performance Measure",
            "en": "운영성과지표",
            "desc": [
                "재무적 지표가 아닌 비재무적 성과 측정지표로, 조직의 효율성(Efficiency) 과 효과성(Effectiveness) 을 평가하는 데 사용됩니다.",
                "이는 모든 사업 부문이 회사의 목표를 달성하기 위해 유기적으로 협력하고 있는지를 판단하는 기준입니다."
            ]
        },
        {
            "term": "Operations",
            "en": "운영활동",
            "desc": [
                "‘운영목표(Operational Objectives)’ 또는 ‘운영통제(Operational Controls)’와 함께 사용되며,",
                "조직의 운영 효율성 및 효과성, 성과 및 수익성 목표, 그리고 자원의 보호(Safeguarding of Resources) 와 관련된 활동을 의미합니다."
            ]
        },
        {
            "term": "Organization",
            "en": "조직",
            "desc": [
                "조직은 이사회(Board of Directors), 경영진(Senior Management), 그리고 그 외 임직원 전체로 구성된 사람들의 집합체를 의미합니다.",
                "즉, 조직의 목표 달성과 내부통제 수행에 관여하는 모든 인적 요소를 포함합니다."
            ]
        },
        {
            "term": "Organizational Chart",
            "en": "조직도",
            "desc": [
                "조직 내 부서, 사업부, 관리자의 역할과 책임 관계를 도식적으로 나타낸 구조도입니다.",
                "즉, 부문 간 관계와 보고체계(Reporting Line) 를 시각적으로 표현하여 조직의 운영 구조를 명확히 보여줍니다."
            ]
        },
        {
            "term": "Output Processing Controls",
            "en": "출력처리 통제",
            "desc": [
                "컴퓨터 처리 과정이 완료된 후부터 사용자에게 데이터가 전달되기까지의 출력 데이터 무결성(Integrity) 을 보장하기 위한 통제입니다.",
                "사용자는 정확하고 완전한 데이터가 적시에 제공되어야 일상 업무를 수행할 수 있으므로, 출력통제는 전산기록 시스템의 핵심 통제 영역 중 하나입니다.",
                "출력통제의 주요 범위는 다음과 같습니다:",
                "- 정보기술 균형 및 대사(IT Balancing & Reconciliation)",
                "- 출력물 배포 관리(Output Distribution)",
                "- 사용자 검증 및 대사(User Balancing & Reconciliation)",
                "- 기록 보존(Record Retention)",
                "- 책임문서 관리(Accountable Document Control)",
                "- 출력오류 처리(Output Error Handling)",
                "즉, 출력통제의 목적은 승인된(Authorized), 완전한(Complete), 정확한(Accurate) 정보가 사용자에게 전달되도록 보장하는 것입니다."
            ]
        }
    ],
    "P": [
        {
            "term": "PDCA Cycle",
            "en": "PDCA 사이클",
            "desc": [
                "모든 품질경영(Quality Management) 프로그램의 핵심 전제는 지속적인 개선(Continuous Improvement) 에 대한 강조입니다.",
                "이 지속적 개선 접근법은 PDCA 사이클(Plan-Do-Check-Act) 로 표현되며,",
                "1930년대 벨시스템(Bell System) 의 쉐워트 박사(Dr. Shewhart) 에 의해 개발되었습니다.",
                "PDCA 사이클은 계획(Plan) → 실행(Do) → 점검(Check) → 조치(Act) 의 네 단계를 포함하며,",
                "‘데밍 휠(Deming Wheel)’ 로도 불리며 품질관리의 핵심 개념 중 하나로 인정받습니다."
            ]
        },
        {
            "term": "Play Script",
            "en": "플레이 스크립트 / 절차 대본 기법",
            "desc": [
                "플레이 스크립트는 절차를 단계별로 명확하게 정의하는 문서화 방식으로, 연극 대본(script)에서 유래된 개념입니다.",
                "각 단계를 따라가기 쉽게 서술함으로써, 업무 절차를 표준화하고 일관성 있게 수행할 수 있도록 돕습니다."
            ]
        },
        {
            "term": "Policy",
            "en": "정책",
            "desc": [
                "경영진 또는 이사회가 내부통제 구현을 위해 해야 할 일(What should be done) 을 명확히 제시한 선언적 문서 또는 방침입니다.",
                "정책은 문서화되어 있거나 공식 커뮤니케이션을 통해 명시될 수도 있으며, 때로는 경영진의 행동이나 의사결정을 통해 암묵적으로 표현되기도 합니다.",
                "정책(Policy) 은 구체적인 실행 절차인 절차(Procedure) 의 기반이 됩니다."
            ]
        },
        {
            "term": "Present and Functioning",
            "en": "존재 및 작동",
            "desc": [
                "내부통제의 구성요소와 원칙에 적용되는 개념으로,",
                "- “존재(Present)” 는 내부통제 구성요소와 관련 원칙이 시스템의 설계 및 실행 단계에서 실제로 존재함을 확인하는 것을 의미합니다.",
                "- “작동(Functioning)” 은 내부통제 구성요소와 원칙이 운영 과정에서도 지속적으로 유지되고 제대로 작동함을 확인하는 것을 뜻합니다.",
                "즉, 내부통제는 단순히 설계상 존재할 뿐 아니라, 실제 운영 중에도 기능하고 있어야 효과적이라고 할 수 있습니다."
            ]
        },
        {
            "term": "Preventive Controls",
            "en": "예방 통제",
            "desc": [
                "통제는 일반적으로 설계 목적에 따라 탐지통제(Detective Controls) 와 예방통제(Preventive Controls) 로 구분됩니다.",
                "예방통제는 오류나 부정행위가 발생하기 전에 이를 방지하도록 설계된 통제활동을 의미합니다.",
                "이러한 통제는 시스템이나 절차 내에 내재적으로 포함되어 있어 눈에 잘 띄지 않을 수도 있지만,",
                "문제 발생 가능성을 사전에 차단하는 핵심적인 역할을 합니다."
            ]
        },
        {
            "term": "Privacy Act of 1974",
            "en": "1974년 개인정보보호법",
            "desc": [
                "미국 연방법으로, 개인정보를 포함하는 데이터의 오용(Misuse) 또는 침해(Compromise) 를 방지하기 위해",
                "연방기관(Federal Agencies) 에 다양한 요구사항을 부과합니다.",
                "특히 자동자료처리기관(ADP Organizations) 은 개인 데이터를 처리할 때,",
                "무단 공개, 파기, 변경으로부터 데이터를 보호하기 위한 합리적인 수준의 보안조치를 취해야 합니다.",
                "이 법은 고의적 행위뿐만 아니라 사고나 부주의로 인한 데이터 유출에도 적용됩니다."
            ]
        },
        {
            "term": "Procedure",
            "en": "절차",
            "desc": [
                "정책(Policy)을 실행하기 위한 구체적인 행동(Activity) 또는 단계적 수행 과정을 의미합니다.",
                "즉, 정책이 “무엇을 해야 하는가”를 정의한다면, 절차는 “어떻게 해야 하는가(How to do it)”를 구체화합니다."
            ]
        },
        {
            "term": "Process Engineering Cycle",
            "en": "프로세스 엔지니어링 사이클",
            "desc": [
                "개별 시스템이나 응용프로그램을 구축(Build) 하는 데 포함된 일련의 활동을 의미합니다.",
                "이 사이클 동안 조직은 위험을 식별하고(Risk Identification), 평가하며(Measurement), 통제수단을 정의·적용(Control Implementation) 합니다.",
                "또한, 업무운영의 효율성(Efficiency) 과 효과성(Effectiveness) 을 평가하고,",
                "현행 비즈니스 요구사항이나 규제요건(Regulatory Requirements) 에 대응하기 위해 시스템을 변경(Change) 또는 업그레이드(Upgrade) 하는 활동도 포함됩니다."
            ]
        },
        {
            "term": "Production or Conversion Cycle",
            "en": "생산 또는 전환 사이클",
            "desc": [
                "조직의 생산계획 및 통제(Production Planning & Control), 재고관리(Inventory Planning & Control),",
                "자산 및 이연비용 회계(Property and Deferred Cost Accounting), 원가회계(Cost Accounting) 등의 기능을 포함하는 사이클입니다.",
                "즉, 자원(원재료, 노동력 등)을 제품 또는 서비스로 전환하는 과정 전반을 포괄합니다."
            ]
        }
    ],
    "R": [
        {
            "term": "Reasonable Assurance",
            "en": "합리적 보증",
            "desc": [
                "내부통제는 아무리 잘 설계되고 운영되더라도, 조직의 목표 달성을 절대적으로 보장할 수는 없다는 개념을 의미합니다.",
                "이는 모든 내부통제 시스템이 가지는 내재적 한계(Inherent Limitations) 때문입니다."
            ]
        },
        {
            "term": "Recording",
            "en": "기록",
            "desc": [
                "거래(Transaction)는 재무제표 작성에 필요한 정보를 제공하고,",
                "일반적으로 인정된 회계원칙(GAAP) 또는 기타 적용 가능한 기준에 따라 보고할 수 있도록 적절히 기록되어야 합니다.",
                "또한, 자산의 책임성과 추적 가능성(Accountability for Assets) 을 유지하기 위해서도 기록이 필요합니다."
            ]
        },
        {
            "term": "Reference Letter",
            "en": "추천서",
            "desc": [
                "내부통제 설계 및 평가 업무 경험을 증명할 수 있는 인사가 작성한 공식 서신입니다.",
                "추천서에는 반드시 직무 경험(Job Experience) 에 대한 구체적인 설명이 포함되어야 합니다."
            ]
        },
        {
            "term": "Relevant Principle",
            "en": "관련 원칙",
            "desc": [
                "‘원칙(Principles)’은 내부통제 구성요소(Components)에 연계된 기본 개념(Fundamental Concept) 을 의미합니다.",
                "다만, 특정 산업, 운영, 또는 규제 환경에서는 일부 원칙이 특정 구성요소와 직접적으로 관련되지 않을 수도 있음을 인정합니다."
            ]
        },
        {
            "term": "Residual Risk",
            "en": "잔여위험",
            "desc": [
                "경영진이 위험에 대한 대응조치(Risk Response) 를 설계하고 실행한 이후에도",
                "여전히 남아 있는 목표 달성 저해 가능성을 의미합니다.",
                "즉, 위험을 완전히 제거할 수 없기 때문에, 대응 이후에도 존재하는 불가피한 위험 수준입니다."
            ]
        },
        {
            "term": "Revenue Cycle",
            "en": "수익 사이클",
            "desc": [
                "조직의 수익창출 활동과 관련된 전 과정을 의미하며, 다음의 기능들을 포함합니다:",
                "- 상품·서비스 요청 접수 및 승인(Receiving and Accepting Orders)",
                "- 상품 인도 및 서비스 제공(Delivery/Provision)",
                "- 신용 승인 및 현금수취(Credit Granting & Cash Receipts)",
                "- 청구 및 수금(Billing & Collection)",
                "- 수익, 외상매출금, 커미션, 보증, 대손, 반품 등의 회계처리(Accounting for Revenue-Related Items)"
            ]
        },
        {
            "term": "Risk",
            "en": "위험",
            "desc": [
                "위험이란 잠재적인 위협(Threat) 이 실제로 발생하여 조직에 부정적인 영향을 미칠 가능성(Likelihood)을 의미합니다.",
                "예를 들어, 허리케인 접근 시 기업 데이터센터 파손의 위험이 증가하는 것처럼,",
                "위험은 항상 발생 확률(Probability) 또는 발생 빈도(Frequency) 와 연관되어 있습니다."
            ]
        },
        {
            "term": "Risk Analysis",
            "en": "위험분석",
            "desc": [
                "조직이 직면한 위험을 식별(Identify), 평가(Assess), 정량화(Quantify) 하여",
                "그 발생 가능성과 영향을 분석하는 과정입니다.",
                "위험분석은 특정 상황에서 성공 확률을 높이고 의사결정의 품질을 개선하기 위한 과정입니다."
            ]
        },
        {
            "term": "Risk Appetite",
            "en": "위험선호도",
            "desc": [
                "조직이 목표 달성을 위해 수용할 의사가 있는 위험 수준(Degree of Risk Willing to Accept) 을 의미합니다.",
                "이는 경영전략 수립 시, 위험과 수익 간 균형을 결정하는 핵심 기준으로 활용됩니다."
            ]
        },
        {
            "term": "Risk Assessment",
            "en": "위험평가",
            "desc": [
                "조직은 자신이 직면한 위험을 인식하고 이에 대응해야 합니다.",
                "이를 위해 판매, 생산, 마케팅, 재무 등 주요 활동과 통합된 목표(Objectives) 를 설정하고,",
                "그 목표 달성에 영향을 미칠 수 있는 위험을 식별·분석·관리하기 위한 메커니즘을 구축해야 합니다."
            ]
        },
        {
            "term": "Risk Exposure – Threat Diagram",
            "en": "위험노출–위협 도식",
            "desc": [
                "- Risk(위험) 은 바람직하지 않은 사건이 발생할 확률(Probability) 을 의미합니다.",
                "- Threat(위협) 은 손실을 유발할 수 있는 사건 또는 활동의 원인(Cause) 을 의미합니다.",
                "- Exposure(노출) 은 위협이 현실화되었을 때 발생할 수 있는 잠재적 손실(Potential Loss) 을 나타냅니다.",
                "따라서 위험노출–위협 도식은 이러한 원인-결과 관계(Cause–Effect Relationship) 를 시각적으로 보여주어, 위험의 구성요소를 명확히 설명합니다."
            ]
        },
        {
            "term": "Risk Management",
            "en": "위험관리",
            "desc": [
                "위험관리는 최고경영진(Senior Management) 의 책임 하에 수행되는 공식적이고 체계적인 과정으로,",
                "위험을 식별(Identify) → 측정(Measure) → 우선순위 결정(Prioritize) 하여",
                "효율적으로 관리할 수 있도록 하는 절차를 포함합니다."
            ]
        },
        {
            "term": "Risk Response",
            "en": "위험대응",
            "desc": [
                "조직이 위험을 관리하기 위해 내리는 의사결정 유형으로, 다음 네 가지 방법이 있습니다:",
                "1. 수용(Accept) – 위험을 감수하고 유지",
                "2. 회피(Avoid) – 위험의 원인을 제거",
                "3. 감소(Reduce) – 발생 가능성 또는 영향을 줄이는 조치",
                "4. 공유(Share) – 보험, 계약, 제휴 등을 통해 위험을 분산"
            ]
        },
        {
            "term": "Risk Scoring",
            "en": "Using External Application Characteristics",
            "desc": [
                "응용프로그램이 개발·운영·사용되는 환경적 특성(Environmental Characteristics) 을 평가하여",
                "해당 응용프로그램의 위험 수준을 산정(Determine) 하는 방법입니다."
            ]
        },
        {
            "term": "Risk Scoring",
            "en": "Using Internal Application Characteristics",
            "desc": [
                "응용프로그램이 처리하는 데이터의 특성(Data Characteristics) 과",
                "그 데이터를 통제하는 내부통제수준(Control Effectiveness) 을 평가하여",
                "응용프로그램의 내재적 위험 수준(Inherent Risk Level) 을 결정하는 방법입니다."
            ]
        },
        {
            "term": "Risk Tolerance",
            "en": "위험허용도",
            "desc": [
                "조직이 목표 달성 과정에서 성과 변동(Performance Variation) 을 수용할 수 있는 허용 범위를 의미합니다.",
                "즉, 위험선호도(Risk Appetite)를 구체화한 개념으로, 실제 운영 수준에서 허용 가능한 변동의 한계치를 정의합니다"
            ]
        }
    ],
    "S": [
        {
            "term": "Sarbanes-Oxley Act",
            "en": "사베인즈-옥슬리법, SOX법",
            "desc": [
                "미국에서 제정된 이 법은 대형 기업 회계부정 및 경영 스캔들(예: 엔론, 월드컴 등) 이후 기업의 책임성과 투명성(Accountability and Transparency) 을 강화하기 위해 마련되었습니다.",
                "이 법의 주요 목적은 기업의 윤리적 경영 기준을 강화하고, 경영진과 이사회에 대한 책임(Responsibility) 을 명확히 하는 것입니다.",
                "SOX법은 경영진에게 다음을 요구합니다:",
                "- 내부통제(Internal Control) 를 정기적으로 검토할 것",
                "- 재무보고의 정확성(Accuracy) 및 완전성(Completeness) 에 대해 책임질 것",
                "또한, 법 위반 시 엄격한 형사 및 민사 처벌을 부과합니다.",
                "이 법은 기업의 경영진(Management) 및 이사회(Board of Directors) 의 책무를 강화하여,",
                "미국 기업지배구조의 신뢰 회복에 큰 전환점을 마련했습니다."
            ]
        },
        {
            "term": "Securities and Exchange Commission",
            "en": "SEC, 미국 증권거래위원회",
            "desc": [
                "미국의 연방정부 기관으로, 증권시장과 투자자 보호, 기업공시의 투명성 확보,",
                "회계감독 및 기업규제 집행을 담당합니다.",
                "즉, 미국 내 금융시장을 감독하고 기업의 공시자료를 검토하는 대표적 규제기관(Regulatory Agency) 입니다."
            ]
        },
        {
            "term": "Segregation of Duties",
            "en": "직무분리",
            "desc": [
                "직원 간의 업무와 책임을 분리(Segregation) 하여 서로의 작업이 자동적으로 검증되도록 하는 내부통제 체계입니다.",
                "이는 견제와 균형(Check and Balance) 을 통해 오류 및 부정을 예방하는 핵심 통제 원칙입니다.",
                "직무분리는 다음 네 가지 유형으로 구성됩니다:",
                "1. 운영(Operation) 과 기록관리(Recordkeeping) 의 분리",
                "2. 자산보관(Custody) 과 회계(Accounting) 의 분리",
                "3. 자산보관(Custody) 과 승인(Authorization) 의 분리",
                "4. 회계업무 간 상호 분리(Accounting Tasks Segregation)",
                "이러한 구조를 통해 자산의 오용이나 오류를 방지하고, 내부통제 환경의 신뢰성을 확보할 수 있습니다."
            ]
        },
        {
            "term": "Segregation of Responsibilities Conflict Matrix",
            "en": "직무책임 분리 충돌 매트릭스",
            "desc": [
                "전통적인 직무분리 개념은 전산화된 환경(Computerized Business Applications) 에서 항상 효과적이지 않을 수 있습니다.",
                "따라서 정보시스템 환경에서는 데이터 단위 수준(Data Element Level) 에서 접근권한을 제한하는 방식으로 직무분리를 구현할 수 있습니다.",
                "이를 위해 충돌 매트릭스(Conflict Matrix) 를 활용하여,",
                "- 분리되어야 할 기능(Function)을 식별하고",
                "- 현재의 분리 수준이 충분한지를 평가(Assessment)합니다.",
                "즉, 전산 시스템 내 세분화된 권한관리(Granular Access Control) 를 통해 내부통제의 효과성을 확보합니다."
            ]
        },
        {
            "term": "Senior Management",
            "en": "고위경영진",
            "desc": [
                "조직의 최고경영자(CEO) 또는 그에 준하는 최고 의사결정자와",
                "그를 보좌하는 고위관리팀(Senior Management Team) 을 포함합니다.",
                "이들은 조직의 전략, 통제, 위험관리 등 핵심 경영활동에 대한 최종 책임을 집니다."
            ]
        },
        {
            "term": "Server",
            "en": "서버",
            "desc": [
                "조직의 데이터가 저장되어 있으며, 여러 컴퓨터가 이를 공유·접근할 수 있도록 하는 중앙 컴퓨터 시스템입니다.",
                "서버는 데이터의 저장, 관리, 접근제어 및 보안 유지를 담당합니다."
            ]
        },
        {
            "term": "Source Document",
            "en": "근거문서 / 원천문서",
            "desc": [
                "회계거래를 기록하기 위한 증빙자료(Evidence)와 데이터(Data) 를 제공하는 문서입니다.",
                "예: 송장(Invoice), 영수증(Receipt), 구매주문서(Purchase Order), 입금전표 등",
                "이 문서는 거래의 발생 근거와 정확성을 입증하는 데 사용됩니다."
            ]
        },
        {
            "term": "Stakeholders",
            "en": "이해관계자",
            "desc": [
                "조직의 활동에 직·간접적으로 영향을 받는 모든 주체를 의미합니다.",
                "예: 주주(Shareholders), 직원(Employees), 고객(Customers), 공급업체(Suppliers),",
                "그리고 조직이 운영되는 지역사회(Community) 등이 이에 해당합니다."
            ]
        },
        {
            "term": "Strategic Planning",
            "en": "전략기획",
            "desc": [
                "조직의 장기적 목표(Long-term Objectives) 와 사업 방향성(Business Direction) 을 결정하는",
                "고차원적 계획수립 과정입니다.",
                "이는 진출 또는 철수할 사업영역 결정, 생산능력 계획(Capacity Planning),",
                "인력계획(Long-range Workforce Planning) 등 조직의 미래지향적 의사결정을 포함합니다."
            ]
        },
        {
            "term": "System Controls",
            "en": "시스템 통제 / 프로세스 통제",
            "desc": [
                "개별 응용시스템(Application System) 의 관리 측면을 정의하는 통제활동입니다.",
                "예를 들어, 급여시스템(Payroll System)의 변경이 효율적이고 정확하게 수행되도록 보장하는 절차,",
                "시스템 구조(System Architecture) 및 문서관리 체계 등을 포함합니다.",
                "또한, 경영진이 시스템 전반을 관리하기 위해 필요한 핵심성과지표(Key Indicators) 를 정의하며,",
                "독립적인 대사(Reconciliation) 를 통해 처리의 정확성을 검증합니다.",
                "시스템 통제의 책임은 주로 중간관리자(Middle Management) 가 집니다."
            ]
        },
        {
            "term": "System of Control",
            "en": "통제시스템",
            "desc": [
                "여러 개별 통제(Individual Controls)로 구성된 통합적 통제체계(Integrated System of Controls) 를 의미합니다.",
                "각 개별 통제는 특정 시점에서 효과적일 수 있지만,",
                "전체적인 통제시스템(System as a Whole) 이 갖추어져야 프로세스 전반이 통제되고 있음을 보장할 수 있습니다.",
                "예를 들어, 우편실에서 수표를 통제하더라도,",
                "은행 입금까지의 전 과정이 통합된 시스템으로 관리되지 않으면 현금수입의 완전한 통제는 불가능합니다.",
                "따라서 중요한 것은 단일 통제가 아닌 전체 통제체계(Systemic Control) 의 완전성과 연계성입니다."
            ]
        }
    ],
    "T": [
        {
            "term": "Tactical Planning",
            "en": "전술계획",
            "desc": [
                "전술계획은 전략계획(Strategic Planning) 을 실행하기 위한 단기적(Short-term) 관리활동 및 실행계획을 의미합니다.",
                "이는 조직의 전략목표를 구체적인 운영활동 요구사항(Operational Requirements) 으로 전환하는 과정입니다.",
                "주로 연간 예산수립(Yearly Budgeting), 성과 모니터링(Performance Monitoring) 등과 같은",
                "단기계획 및 통제 사이클을 포함하며, 경영 기반의 통제시스템 중 가장 강력한 형태 중 하나로 평가됩니다."
            ]
        },
        {
            "term": "Technology General Controls",
            "en": "기술 일반통제 / IT 일반통제, ITGC",
            "desc": [
                "조직의 기술 인프라가 지속적이고 적절하게 운영되도록 보장하는 통제활동을 의미합니다.",
                "이에는 다음과 같은 영역이 포함됩니다:",
                "- 기술 인프라 통제(Technology Infrastructure Controls)",
                "- 보안관리(Security Management)",
                "- 기술의 획득·개발·유지관리(Acquisition, Development, and Maintenance Controls)",
                "또한, 이 통제는 일반 전산통제(General Computer Controls) 또는 정보기술통제(Information Technology Controls) 라고도 불립니다."
            ]
        },
        {
            "term": "Technology — Software",
            "en": "기술 — 소프트웨어",
            "desc": [
                "컴퓨터에서 실행되는 응용프로그램(Application), 제조 통제시스템(Manufacturing Control Systems) 등을 포함한",
                "소프트웨어 기술 전반을 의미합니다."
            ]
        },
        {
            "term": "Threat",
            "en": "위협",
            "desc": [
                "위협이란 특정한 상황에서 발생할 수 있으며, 조직에 손실(Loss) 을 초래할 수 있는 사건(Event) 또는 행위를 의미합니다.",
                "위협에는 내재적 위험(Inherent Hazards) 과 환경적 위험(Environmental Hazards) 이 모두 포함되며,",
                "이는 취약점(Vulnerability) 이 노출될 때 손실을 유발할 가능성이 있습니다."
            ]
        },
        {
            "term": "Threat Point Matrix",
            "en": "위협 포인트 매트릭스",
            "desc": [
                "통제 약점(Control Weakness) 으로 인해 손실 가능성이 높은 시스템 내 지점을 식별하기 위한 도구입니다.",
                "이 매트릭스는 다음과 같이 구성됩니다:",
                "- 한 축에는 통제 포인트(Control Points)",
                "- 다른 축에는 위협 포인트(Threat Points)",
                "해당 분야에 숙련된 전문가가 이 매트릭스를 활용할 경우, 손실 가능성이 높은 영역을 신뢰성 있게 식별할 수 있습니다."
            ]
        },
        {
            "term": "Tone at the Top",
            "en": "최고경영층의 태도",
            "desc": [
                "“Tone at the Top”은 조직문화와 윤리의 방향성을 최고경영층이 설정하는 것을 의미합니다.",
                "이는 단순히 지침을 내리는 것을 넘어, 경영진이 직접 모범을 보이는 행동(‘Walking the Talk’) 을 통해",
                "직원들에게 올바른 가치와 행동 기준을 전달해야 함을 뜻합니다.",
                "즉, 경영진의 윤리적 태도와 행동은 조직 전반의 내부통제 문화 형성에 결정적인 영향을 미칩니다."
            ]
        },
        {
            "term": "Transaction Conflict Matrix",
            "en": "거래 충돌 매트릭스",
            "desc": [
                "재무 관련 거래(Financial Transactions) 에 대한 적절한 직무분리(Segregation of Duties) 여부를 시각적으로 보여주는 도구입니다.",
                "매트릭스는 거래가 수행할 수 있는 기능(Function)과 영향을 미치는 재무계정(Account)을 대응시켜 표시합니다.",
                "동일한 거래가 하나의 계정에 대해 과도하게 많은 기능을 수행할 수 있을 경우,",
                "이는 통제 충돌(Control Conflict) 가능성을 나타내며, 내부통제의 약점(Risk of Control Weakness) 으로 간주됩니다."
            ]
        },
        {
            "term": "Transaction Controls",
            "en": "거래통제",
            "desc": [
                "조직의 비즈니스 프로세스에서 발생하는 거래처리 위험(Transaction Processing Risks) 을 완화하기 위한 통제활동입니다.",
                "이 통제는 수작업(Manual) 또는 자동화(Automated) 형태로 운영될 수 있으며,",
                "다음의 정보처리 목적을 달성하도록 설계됩니다:",
                "- 완전성(Completeness)",
                "- 정확성(Accuracy)",
                "- 유효성(Validity)"
            ]
        },
        {
            "term": "Transaction Entry Controls",
            "en": "거래입력통제, IT",
            "desc": [
                "비즈니스 거래가 발생한 시점부터 결과가 사용자에게 전달되기 전까지의 전 과정을 통제하는 절차입니다.",
                "이 통제는 데이터가 시스템에 입력될 때의 정확성과 완전성을 보장합니다.",
                "통제 범위에는 다음이 포함됩니다:",
                "- 데이터 통신 링크 입력 전까지의 통제(Pre-Communication Link Controls)",
                "- 비통신 환경에서의 응용프로그램 입력 전 통제(Application Input Controls)",
                "거래입력통제는 수작업 및 자동통제 절차의 조합으로 구성되며,",
                "특히 데이터 변환(Data Conversion), 검토 및 유효성 검증(Edit and Validation) 단계에서 중요합니다.",
                "최근에는 자동화된 통제(Auto-Control Routine) 의 비중이 커지고 있으며,",
                "이는 일관성(Consistency) 과 효율성(Efficiency) 을 향상시킵니다."
            ]
        },
        {
            "term": "Transaction Origination Controls",
            "en": "거래발생통제",
            "desc": [
                "데이터가 시스템에 입력되기 전에 그 정확성과 완전성을 확보하기 위한 통제입니다.",
                "이 통제는 데이터가 기계 판독이 가능한 형식(Machine-Readable Format) 으로 변환되기 전까지의 절차에 적용됩니다.",
                "목표는 다음과 같습니다:",
                "- 입력 데이터의 유효성(Validity) 확보",
                "- 데이터의 신뢰성(Reliability) 확보",
                "- 정보의 비용 효율성(Cost-Effectiveness) 보장",
                "- 데이터가 손상 또는 조작되지 않도록 보호(Protection from Compromise)",
                "관리자, 시스템 담당자, 통제 설계자는 거래발생통제의 강화를 통해",
                "시스템 입력 단계에서부터 데이터 품질(Data Quality) 과 통제 신뢰성(Control Reliability) 을 확보해야 합니다."
            ]
        },
        {
            "term": "Transaction Processing Risk",
            "en": "거래처리위험",
            "desc": [
                "단일 비즈니스 이벤트가 발생 시점부터 완료 시점까지 처리되는 과정에서 발생할 수 있는 위험입니다.",
                "즉, 거래의 입력, 처리, 저장, 보고 과정에서 오류, 누락, 부정행위, 시스템 실패 등이 발생할 위험을 포함합니다."
            ]
        },
        {
            "term": "Transaction Use Matrix",
            "en": "거래사용 매트릭스",
            "desc": [
                "데이터 항목(Data Items) 과 해당 데이터를 사용하는 프로그램(Programs) 간의 관계를 나타내는 도표입니다.",
                "한쪽에는 직원 이름, 사번, 순급여, 사회보장번호 등의 데이터 항목이 나열되고,",
                "다른 쪽에는 프로그램이 수행하는 기능(Function) 이 표시됩니다.",
                "이를 통해 어떤 프로그램이 어떤 데이터에 접근·활용하는지 시각적으로 파악할 수 있습니다.",
                "이 매트릭스는 데이터 접근통제(Data Access Control) 및 권한 분리(Authorization Segregation) 평가에 활용됩니다."
            ]
        }
    ],
    "V": [
        {
            "term": "Vulnerability",
            "en": "취약점",
            "desc": [
                "취약점은 비즈니스 시스템(프로세스)의 흐름 내에 존재하는 약점(Weakness) 으로,",
                "조직이 손실(Loss) 에 노출될 가능성을 높이는 요인을 의미합니다.",
                "예를 들어, 컴퓨터 단말기에 암호를 메모지에 붙여두는 경우,",
                "무단 접근 권한이 없는 사용자(Unauthorized User) 가 이를 악용해",
                "조직에 피해를 초래할 수 있습니다.",
                "즉, 취약점은 위협(Threat) 이 현실화될 수 있는 통제상의 빈틈(Control Gap) 으로,",
                "조직의 자산·정보·운영에 부정적인 영향을 미칠 수 있는 요소입니다."
            ]
        }
    ]


}
</script>

<style scoped>
/* 기본 타이포 규칙 그대로 유지 */
.dict {
  font-family: 'Noto Sans KR', sans-serif;
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 16px;
}

.dict__title {
  font-size: 1.625rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #0f172a;
}

p { font-size: 0.95rem; }

/* =========================
 * 탭: grid + 자동 줄바꿈
 * ========================= */
.dict__tabs {
  display: grid;
  grid-template-columns: repeat(13, minmax(40px, 1fr));
  gap: 8px;
  margin: 12px 0 18px;
}

.tab {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 4px;
  text-align: center;
  font-weight: 700;
  font-size: 0.95rem;
  background: #fff;
  cursor: pointer;
  min-height: 40px;              /* 터치 타깃 보장 */
  line-height: 1;
}
.tab.active {
  background: #1d2088;
  color: #fff;
  border-color: #1d2088;
}

/* =========================
 * 표(리스트) 그리드
 * ========================= */
.t {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}

.t__head {
  display: grid;
  grid-template-columns: 100px 1fr;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
}

.th {
  padding: 12px 14px;
  font-weight: 700;
  font-size: 0.95rem;
  color: #334155;
  text-align: center;
}

.t__body { list-style: none; margin: 0; padding: 0; }

/* 행/아코디언 */
.row { border-bottom: 1px solid #eef0f3; cursor: pointer; }
.row:last-child { border-bottom: 0; }

.row__head {
  display: grid;
  grid-template-columns: 70px 1fr 24px;
  align-items: center;
  padding: 12px 14px;
  background: #fff;
  gap: 20px;
  border: none;
  cursor: pointer;
  width: 100%;
}

.td--no { text-align: center; color: #475569; }

.term { font-weight: 700; color: #0f172a; }
.term--en { color: #64748b; font-weight: 500; margin-left: 6px; }

/* 화살표 */
.caret {
  width: 10px; height: 10px;
  border-right: 2px solid #94a3b8;
  border-bottom: 2px solid #94a3b8;
  transform: rotate(-45deg);
  transition: transform .2s ease;
}
[aria-expanded="true"] .caret { transform: rotate(45deg); }

/* 패널 & 애니메이션 */
.row__panel {
  padding: 12px 14px 16px 100px;  /* 번호열만큼 들여쓰기 */
  background: #fafafa;
  border-top: 1px dashed #e5e7eb;
}
.row__panel p + p { margin-top: 6px; }

.accord-enter-from,
.accord-leave-to { max-height: 0; opacity: 0; }
.accord-enter-to,
.accord-leave-from { max-height: 400px; opacity: 1; }
.accord-enter-active,
.accord-leave-active { transition: max-height .22s ease, opacity .22s ease; }

.empty {
  text-align: center;
  padding: 16px;
  background: #fafafa;
  color: #64748b;
  font-size: 0.95rem;
}

/* =========================
 * 반응형 브레이크포인트
 * ========================= */

/* 1024px 이하: 번호열/패딩 축소, 탭 자동-맞춤 */
@media (max-width: 1024px) {
  .dict { padding: 20px 14px; }
  .dict__tabs {
    grid-template-columns: repeat(13, minmax(36px, 1fr));
    gap: 6px;
  }
  .t__head {
    grid-template-columns: 80px 1fr;
  }
  .row__head {
    grid-template-columns: 60px 1fr 20px;
    gap: 14px;
  }
  .row__panel { padding-left: 80px; }
}

/* 768px 이하: 탭 칼럼 수 유동화(auto-fit), 글자 크기 살짝 축소 */
@media (max-width: 768px) {
  .dict__title { margin-bottom: 12px; }

  /* auto-fit + minmax로 칼럼 수 자동 조절 */
  .dict__tabs {
    grid-template-columns: repeat(auto-fit, minmax(36px, 1fr));
    gap: 6px;
  }
  .tab { font-size: 0.9rem; min-height: 38px; padding: 9px 2px; }

  .t__head {
    grid-template-columns: 68px 1fr;
  }
  .th { padding: 10px 12px; font-size: 0.9rem; }

  .row__head { padding: 10px 12px; }
  .td--no { font-size: 0.9rem; }
  .term { font-size: 0.95rem; }
  .term--en { font-size: 0.9rem; }
  .row__panel { padding: 10px 12px 14px 68px; }
}

/* 600px 이하: 초소형 기기 대응 — 탭 줄바꿈 여유, 번호/화살표 더 컴팩트 */
@media (max-width: 600px) {
  .dict { padding: 16px 12px; }
  .dict__tabs {
    grid-template-columns: repeat(auto-fit, minmax(32px, 1fr));
    gap: 6px;
    row-gap: 8px;
  }
  .tab { min-height: 36px; font-size: 0.875rem; }

  .t__head { grid-template-columns: 56px 1fr; }
  .row__head {
    grid-template-columns: 52px 1fr 18px;
    gap: 10px;
  }
  .caret { width: 9px; height: 9px; border-width: 2px; }
  .row__panel { padding-left: 56px; }
}

/* 420px 이하: 아주 작은 화면 — 타이틀 간격/폰트 살짝 경량화 */
@media (max-width: 420px) {
  .dict__title { font-size: 1.5rem; }
  .tab { font-size: 0.85rem; }
  p, .th, .term { font-size: 0.9rem; }
}

/* 모션 최소화 환경 배려 */
@media (prefers-reduced-motion: reduce) {
  .accord-enter-active,
  .accord-leave-active,
  .caret { transition: none; }
}

</style>
